import { state } from '../state';
import { get, post, API_BASE } from '../api';
import { showToast, errToast } from '../components/toast';
import { confirmModal } from '../components/modal';
import { showContextMenu } from '../components/context-menu';
import type { ContextMenuItem } from '../components/context-menu';
import { openProfileCard } from '../components/profile-card';
import { openThreadPanel, fetchThreadSummaries, renderThreadBadge, closeThreadPanel } from './threads';
import { fetchThreadStatusBatch } from './dev-sessions';
import { realtime, channelTopic } from '../realtime';
import type { RealtimeEvent } from '../realtime';
import { avatarBg } from '../util';
import {
  mountMessageCompose,
  type ComposeHandle,
} from '../components/message-compose';
import { notify, isChannelMuted } from '../components/notifications';
import { fmtFull, fmtDayHeading, fmtMessageHeaderTime } from '../util/time';
import { iconReplyArrow } from '../icons';

/** Message from the API */
interface Attachment {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  url: string;
}

interface Message {
  id: string;
  server_id: string;
  channel: string;
  author_id: string;
  content: string;
  edited_at: string | null;
  reply_to: string | null;
  attachments: string;
  reactions: string;
  pinned: number;
  created_at: string;
  /** Optional system-message kind (joined, left, pinned, channel_created, …).
   *  Discord parity: when set, the row renders as a centered compact line with
   *  a faint icon instead of the normal author/avatar layout. Backend may not
   *  yet emit this field — absence falls through to the normal renderer. */
  kind?: 'system' | 'normal';
  system_event?: string;
  read_by_named_guest?: boolean;
}

const READ_ONLY_CHANNELS: Record<string, string[]> = {
  'dev-activity': ['ai', 'dev'],
};

async function canComposeInChannel(channelId: string, serverId: string): Promise<boolean> {
  const ch = state.channels.find(c => c.id === channelId);
  const channelName = ch?.name || '';
  const requiredRoles = READ_ONLY_CHANNELS[channelName];
  if (!requiredRoles) return true;

  const server = state.servers.find(s => s.id === serverId);
  if (server?.owner_id === state.ownerId) return true;

  const pid = state.ownerId || '';
  if (!pid) return false;
  try {
    const myRoles = await get<Array<{ name: string }>>(`/api/roles/member?server_id=${encodeURIComponent(serverId)}&person_id=${encodeURIComponent(pid)}`);
    return myRoles.some(r => requiredRoles.some(req => r.name.toLowerCase().includes(req)));
  } catch {
    return false;
  }
}

/** Pending file attachments before sending */
let pendingFiles: File[] = [];

/** Cached handle returned by mountMessageCompose — source of truth for
 *  reply state, draft persistence, send button visibility, and attach
 *  wiring on the channel chat surface. */
let composeHandle: ComposeHandle | null = null;

/** Show the reply chip for a target message. Thin wrapper around the
 *  compose handle so the many call sites don't each thread through it. */
function openReplyBarFor(msg: Message): void {
  if (!composeHandle) return;
  composeHandle.setReplyTo({
    id: msg.id,
    authorName: authorName(msg.author_id),
    authorColor: authorColor(msg.author_id),
    snippet: msg.content || '',
    mentionOn: true,
  });
  composeHandle.focus();
}

/** Cache of loaded messages for reply lookups */
let messageCache: Map<string, Message> = new Map();

/** Escape HTML */
function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

/** Per-message header timestamp. Delegates to util/time.fmtMessageHeaderTime
 *  so chat.ts, threads.ts, and pages/dms.ts share one Discord-1:1 implementation
 *  (HUB_TZ + HUB_LOCALE + leading-zero numeric older fallback). */
function formatTime(iso: string): string {
  return fmtMessageHeaderTime(iso);
}

type SyntaxRule = [RegExp, string];

const SYNTAX_RULES: Record<string, SyntaxRule[]> = {
  html: [
    [/(&lt;!--[\s\S]*?--&gt;)/g, '<span class="syn-comment">$1</span>'],
    [/(&lt;\/?)([\w-]+)/g, '$1<span class="syn-tag">$2</span>'],
    [/\b(class|id|href|src|style|type|name|value|alt|title|data-[\w-]+)=/g, '<span class="syn-attr">$1</span>='],
    [/(&quot;[^&]*?&quot;|&#39;[^&]*?&#39;)/g, '<span class="syn-string">$1</span>'],
  ],
  xml: [
    [/(&lt;!--[\s\S]*?--&gt;)/g, '<span class="syn-comment">$1</span>'],
    [/(&lt;\/?)([\w:-]+)/g, '$1<span class="syn-tag">$2</span>'],
    [/([\w:-]+)=/g, '<span class="syn-attr">$1</span>='],
    [/(&quot;[^&]*?&quot;)/g, '<span class="syn-string">$1</span>'],
  ],
  css: [
    [/(\/\*[\s\S]*?\*\/)/g, '<span class="syn-comment">$1</span>'],
    [/([.#][\w-]+)/g, '<span class="syn-tag">$1</span>'],
    [/\b(color|background|display|flex|grid|margin|padding|border|font|width|height|position|top|left|right|bottom|overflow|opacity|transform|transition|animation|z-index|gap|align-items|justify-content)\b/g, '<span class="syn-attr">$1</span>'],
    [/:\s*([^;{}\n]+)/g, ': <span class="syn-string">$1</span>'],
  ],
  javascript: [
    [/(\/\/.*$)/gm, '<span class="syn-comment">$1</span>'],
    [/(\/\*[\s\S]*?\*\/)/g, '<span class="syn-comment">$1</span>'],
    [/\b(const|let|var|function|return|if|else|for|while|class|import|export|from|default|async|await|new|this|typeof|instanceof|try|catch|throw|switch|case|break|continue|null|undefined|true|false|void)\b/g, '<span class="syn-keyword">$1</span>'],
    [/(&quot;[^&]*?&quot;|&#39;[^&]*?&#39;|`[^`]*?`)/g, '<span class="syn-string">$1</span>'],
    [/\b(\d+\.?\d*)\b/g, '<span class="syn-number">$1</span>'],
    [/\b([A-Z]\w+)\b/g, '<span class="syn-tag">$1</span>'],
  ],
  typescript: [
    [/(\/\/.*$)/gm, '<span class="syn-comment">$1</span>'],
    [/(\/\*[\s\S]*?\*\/)/g, '<span class="syn-comment">$1</span>'],
    [/\b(const|let|var|function|return|if|else|for|while|class|import|export|from|default|async|await|new|this|typeof|instanceof|try|catch|throw|switch|case|break|continue|null|undefined|true|false|void|type|interface|enum|as|implements|extends|readonly|private|public|protected|static|abstract|declare|module|namespace)\b/g, '<span class="syn-keyword">$1</span>'],
    [/(&quot;[^&]*?&quot;|&#39;[^&]*?&#39;|`[^`]*?`)/g, '<span class="syn-string">$1</span>'],
    [/\b(\d+\.?\d*)\b/g, '<span class="syn-number">$1</span>'],
    [/\b(string|number|boolean|any|never|unknown|Record|Array|Promise|Partial|Required|Omit|Pick)\b/g, '<span class="syn-type">$1</span>'],
    [/\b([A-Z]\w+)\b/g, '<span class="syn-tag">$1</span>'],
  ],
  json: [
    [/(&quot;[^&]*?&quot;)\s*:/g, '<span class="syn-attr">$1</span>:'],
    [/:\s*(&quot;[^&]*?&quot;)/g, ': <span class="syn-string">$1</span>'],
    [/\b(true|false|null)\b/g, '<span class="syn-keyword">$1</span>'],
    [/\b(\d+\.?\d*)\b/g, '<span class="syn-number">$1</span>'],
  ],
  sql: [
    [/(--.*$)/gm, '<span class="syn-comment">$1</span>'],
    [/\b(SELECT|INSERT|UPDATE|DELETE|FROM|WHERE|INTO|VALUES|SET|CREATE|ALTER|DROP|TABLE|INDEX|JOIN|LEFT|RIGHT|INNER|OUTER|ON|AND|OR|NOT|IN|IS|NULL|AS|ORDER|BY|GROUP|HAVING|LIMIT|OFFSET|UNION|EXISTS|BETWEEN|LIKE|DISTINCT|PRIMARY|KEY|FOREIGN|REFERENCES|DEFAULT|CASCADE|CONSTRAINT|ADD|COLUMN|IF|BEGIN|COMMIT|ROLLBACK|INTEGER|TEXT|REAL|BLOB)\b/gi, '<span class="syn-keyword">$1</span>'],
    [/(&#39;[^&]*?&#39;)/g, '<span class="syn-string">$1</span>'],
    [/\b(\d+\.?\d*)\b/g, '<span class="syn-number">$1</span>'],
  ],
  rust: [
    [/(\/\/.*$)/gm, '<span class="syn-comment">$1</span>'],
    [/\b(fn|let|mut|pub|use|mod|struct|enum|impl|trait|self|Self|match|if|else|for|while|loop|return|async|await|move|ref|where|type|const|static|unsafe|extern|crate|super|as|in|dyn|Box|Vec|Option|Result|String|Ok|Err|Some|None|true|false)\b/g, '<span class="syn-keyword">$1</span>'],
    [/(&quot;[^&]*?&quot;)/g, '<span class="syn-string">$1</span>'],
    [/\b(\d+\.?\d*)\b/g, '<span class="syn-number">$1</span>'],
    [/\b([A-Z]\w+)\b/g, '<span class="syn-tag">$1</span>'],
  ],
  bash: [
    [/(#.*$)/gm, '<span class="syn-comment">$1</span>'],
    [/\b(if|then|else|fi|for|do|done|while|case|esac|function|return|local|export|source|echo|exit|cd|ls|rm|cp|mv|mkdir|chmod|chown|grep|sed|awk|curl|ssh|sudo|docker|git|npm|npx)\b/g, '<span class="syn-keyword">$1</span>'],
    [/(&quot;[^&]*?&quot;|&#39;[^&]*?&#39;)/g, '<span class="syn-string">$1</span>'],
    [/(\$[\w{}]+)/g, '<span class="syn-tag">$1</span>'],
  ],
  python: [
    [/(#.*$)/gm, '<span class="syn-comment">$1</span>'],
    [/\b(def|class|if|elif|else|for|while|return|import|from|as|try|except|finally|raise|with|yield|lambda|pass|break|continue|and|or|not|in|is|None|True|False|self|async|await|global|nonlocal)\b/g, '<span class="syn-keyword">$1</span>'],
    [/(&quot;{3}[\s\S]*?&quot;{3}|&#39;{3}[\s\S]*?&#39;{3}|&quot;[^&]*?&quot;|&#39;[^&]*?&#39;)/g, '<span class="syn-string">$1</span>'],
    [/\b(\d+\.?\d*)\b/g, '<span class="syn-number">$1</span>'],
    [/\b([A-Z]\w+)\b/g, '<span class="syn-tag">$1</span>'],
  ],
};

const LANG_ALIASES: Record<string, string> = {
  js: 'javascript', ts: 'typescript', sh: 'bash', shell: 'bash',
  zsh: 'bash', ps1: 'bash', powershell: 'bash', py: 'python',
  rb: 'python', yml: 'json', yaml: 'json', toml: 'json',
  htm: 'html', svg: 'xml', jsx: 'javascript', tsx: 'typescript',
  rs: 'rust', sql: 'sql', sqlite: 'sql', mysql: 'sql', psql: 'sql',
};

function highlightSyntax(code: string, lang: string): string {
  const escaped = esc(code);
  const resolved = LANG_ALIASES[lang.toLowerCase()] || lang.toLowerCase();
  const rules = SYNTAX_RULES[resolved];
  if (!rules) return escaped;
  let result = escaped;
  for (const [pattern, replacement] of rules) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

/** Markdown renderer with Discord-parity formatting + syntax highlighting. */
export function renderMarkdown(text: string): string {
  // First handle code blocks (triple backticks) before escaping. Discord parity:
  // wrap every fenced block with a header row showing the language label (or
  // "plaintext" when no language hint was given) and a Copy button. The
  // `data-code-raw` attribute lets the copy handler grab the original source
  // without having to re-decode the rendered innerHTML.
  const codeBlocks: string[] = [];
  const withPlaceholders = text.replace(/```(\w*)\n?([\s\S]*?)```/g, (_match, lang, code) => {
    const idx = codeBlocks.length;
    const body = code.trim();
    const label = (lang && String(lang).trim()) ? String(lang).trim() : 'plaintext';
    const highlighted = highlightSyntax(body, label);
    codeBlocks.push(
      `<div class="msg-codeblock-wrap" data-code-raw="${esc(body)}">`
      + `<div class="msg-codeblock-header">`
      + `<span class="msg-codeblock-lang">${esc(label)}</span>`
      + `<button type="button" class="msg-codeblock-copy" title="Copy code" aria-label="Copy code">`
      + `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`
      + `<span class="msg-codeblock-copy-label">Copy</span>`
      + `</button>`
      + `</div>`
      + `<pre class="msg-codeblock"><code${lang ? ` class="lang-${esc(lang)}"` : ''}>${highlighted}</code></pre>`
      + `</div>`
    );
    return `CBLK${idx}CBLK`;
  });

  let html = esc(withPlaceholders)
    .replace(/^#{3}\s+(.+)$/gm, '<h4 class="msg-h3">$1</h4>')
    .replace(/^#{2}\s+(.+)$/gm, '<h3 class="msg-h2">$1</h3>')
    .replace(/^#\s+(.+)$/gm, '<h2 class="msg-h1">$1</h2>')
    .replace(/^-{3,}$/gm, '<hr class="msg-hr">')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/~~(.+?)~~/g, '<del>$1</del>')
    .replace(/__(.+?)__/g, '<u>$1</u>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\|\|(.+?)\|\|/g, '<span class="msg-spoiler" onclick="this.classList.toggle(\'revealed\')">$1</span>')
    .replace(/`(.+?)`/g, '<code class="msg-inline-code">$1</code>')
    .replace(/^&gt; (.+)$/gm, '<blockquote class="msg-blockquote">$1</blockquote>')
    .replace(/^- (.+)$/gm, '<div class="msg-bullet">$1</div>')
    .replace(/^\d+\. (.+)$/gm, '<div class="msg-bullet msg-bullet-ordered">$1</div>')
    .replace(/(https?:\/\/[^\s]+)/g, (url) => {
      try {
        const u = new URL(url);
        const domain = u.hostname.replace('www.', '');
        return `<a href="${url}" target="_blank" rel="noopener" class="msg-link">${domain}<span class="msg-link-path">${u.pathname !== '/' ? u.pathname : ''}</span></a>`;
      } catch { return `<a href="${url}" target="_blank" rel="noopener">${url}</a>`; }
    })
    .replace(/\n/g, '<br>');

  // @mentions — highlight @name references
  html = html.replace(/@(\w+)/g, (match, name) => {
    const person = state.people.find(p =>
      p.name.toLowerCase() === name.toLowerCase() ||
      p.name.toLowerCase().replace(/\s+/g, '') === name.toLowerCase()
    );
    if (person) {
      return `<span class="mention" data-person="${esc(person.id)}">@${esc(person.name)}</span>`;
    }
    if (name === 'everyone' || name === 'here') {
      return `<span class="mention mention-everyone">@${esc(name)}</span>`;
    }
    return match;
  });

  // #channel mentions — highlight #channel-name references
  html = html.replace(/#([\w-]+)/g, (match, name) => {
    const channel = state.channels.find(c =>
      c.server_id === state.currentServer && c.name.toLowerCase() === name.toLowerCase()
    );
    if (channel) {
      return `<span class="mention mention-channel" data-channel="${esc(channel.name)}">#${esc(channel.name)}</span>`;
    }
    return match;
  });

  // Restore code blocks
  for (let i = 0; i < codeBlocks.length; i++) {
    html = html.replace(`CBLK${i}CBLK`, codeBlocks[i] ?? '');
  }

  return html;
}

/** Mention autocomplete state */
let mentionPopup: HTMLElement | null = null;
let mentionQuery = '';
let mentionStart = -1;
let mentionMatches: MentionablePerson[] = [];
let mentionSelectedIdx = 0;

interface MentionablePerson {
  id: string;
  name: string;
  role: string;
}

function mentionablePeople(): MentionablePerson[] {
  // Limit to members of the current server when known; fall back to all people.
  // role is the server-member role label (admin/member/owner) when present,
  // else the person's global role string. Used to colour the dropdown row.
  const sid = state.currentServer;
  const all = state.people;
  if (!sid) return all.map(p => ({ id: p.id, name: p.name, role: p.role || '' }));
  const members = state.serverMembers.filter(m => m.server_id === sid);
  const memberIds = new Set(members.map(m => m.person_id));
  const memberRole = (pid: string): string => members.find(m => m.person_id === pid)?.role || '';
  const inServer = all.filter(p => memberIds.has(p.id));
  const source = inServer.length > 0 ? inServer : all;
  return source.map(p => ({ id: p.id, name: p.name, role: memberRole(p.id) || p.role || '' }));
}

export function showMentionAutocomplete(input: HTMLTextAreaElement): void {
  const text = input.value;
  const pos = input.selectionStart ?? text.length;

  // Find @ before cursor
  let atIdx = -1;
  for (let i = pos - 1; i >= 0; i--) {
    if (text[i] === '@') { atIdx = i; break; }
    if (text[i] === ' ' && i < pos - 1) break;
  }

  if (atIdx === -1) {
    hideMentionAutocomplete();
    return;
  }

  mentionStart = atIdx;
  mentionQuery = text.substring(atIdx + 1, pos).toLowerCase();

  mentionMatches = mentionablePeople().filter(p =>
    p.name.toLowerCase().includes(mentionQuery) ||
    p.name.toLowerCase().replace(/\s+/g, '').includes(mentionQuery)
  ).slice(0, 8);
  mentionSelectedIdx = 0;

  if (mentionMatches.length === 0) {
    hideMentionAutocomplete();
    return;
  }

  if (!mentionPopup) {
    mentionPopup = document.createElement('div');
    mentionPopup.className = 'mention-popup';
    // Discord parity: anchor to <body> with position:fixed so the popover
    // escapes the compose's overflow:hidden box and doesn't get clipped on
    // mobile / cramped layouts. Position is recomputed each render below.
    mentionPopup.style.position = 'fixed';
    document.body.appendChild(mentionPopup);
  }

  renderMentionMatches(input);
  mentionPopup.style.display = 'block';
  // Anchor above the textarea — match Discord's "popover sits on top of the
  // compose row" placement. Recompute every time so resizes / scrolls are
  // tracked. We use bottom-anchored positioning so the popup grows upward as
  // matches are added.
  const r = input.getBoundingClientRect();
  mentionPopup.style.left = `${r.left}px`;
  mentionPopup.style.bottom = `${Math.max(8, window.innerHeight - r.top + 6)}px`;
  mentionPopup.style.width = `${Math.min(360, Math.max(240, r.width))}px`;
  mentionPopup.style.maxHeight = '320px';
  mentionPopup.style.overflowY = 'auto';
  mentionPopup.style.zIndex = '300';
}

function renderMentionMatches(input: HTMLTextAreaElement): void {
  if (!mentionPopup) return;
  // Discord parity: each row shows a coloured avatar (initial), the display
  // name, and a small role tag on the right when the person has one.
  mentionPopup.innerHTML = mentionMatches.map((p, i) => {
    const initial = p.name.charAt(0).toUpperCase();
    const roleTag = p.role ? `<span class="mention-role">${esc(p.role)}</span>` : '';
    return `
    <div class="mention-item ${i === mentionSelectedIdx ? 'active' : ''}" data-idx="${i}">
      <span class="mention-avatar" style="background:${avatarBg(p.id)}">${esc(initial)}</span>
      <span class="mention-name">${esc(p.name)}</span>
      ${roleTag}
    </div>`;
  }).join('');
  mentionPopup.querySelectorAll<HTMLElement>('.mention-item').forEach(el => {
    el.addEventListener('mousedown', (e) => {
      e.preventDefault();
      const idx = parseInt(el.dataset['idx'] ?? '0');
      selectMentionMatch(input, idx);
    });
    el.addEventListener('mouseenter', () => {
      const idx = parseInt(el.dataset['idx'] ?? '0');
      mentionSelectedIdx = idx;
      renderMentionMatches(input);
    });
  });
}

function selectMentionMatch(input: HTMLTextAreaElement, idx: number): void {
  const match = mentionMatches[idx];
  if (!match) return;
  const text = input.value;
  const pos = input.selectionStart ?? text.length;
  const before = text.substring(0, mentionStart);
  const after = text.substring(pos);
  const token = `@${match.name.replace(/\s+/g, '')}`;
  input.value = `${before}${token} ${after}`;
  const cursor = before.length + token.length + 1;
  input.selectionStart = input.selectionEnd = cursor;
  input.focus();
  hideMentionAutocomplete();
}

/** Called from the chat-input keydown handler BEFORE the Enter=send path so
 *  selecting a mention doesn't also send the message. Returns true if the
 *  key was consumed. */
export function handleMentionKeydown(e: KeyboardEvent, input: HTMLTextAreaElement): boolean {
  if (!mentionPopup || mentionPopup.style.display === 'none' || mentionMatches.length === 0) return false;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    mentionSelectedIdx = (mentionSelectedIdx + 1) % mentionMatches.length;
    renderMentionMatches(input);
    return true;
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    mentionSelectedIdx = (mentionSelectedIdx - 1 + mentionMatches.length) % mentionMatches.length;
    renderMentionMatches(input);
    return true;
  }
  if (e.key === 'Enter' || e.key === 'Tab') {
    e.preventDefault();
    selectMentionMatch(input, mentionSelectedIdx);
    return true;
  }
  if (e.key === 'Escape') {
    e.preventDefault();
    hideMentionAutocomplete();
    return true;
  }
  return false;
}

function hideMentionAutocomplete(): void {
  if (mentionPopup) {
    mentionPopup.style.display = 'none';
  }
}

/* ── Slash Commands Autocomplete ── */

interface SlashCommand {
  name: string;
  desc: string;
  execute: (input: HTMLTextAreaElement) => void;
}

const SLASH_COMMANDS: SlashCommand[] = [
  { name: '/shrug', desc: 'Appends ¯\\_(ツ)_/¯ to your message', execute: (input) => { input.value = input.value.replace(/^\/shrug\s*/, '') + ' ¯\\_(ツ)_/¯'; } },
  { name: '/tableflip', desc: 'Appends (╯°□°)╯︵ ┻━┻', execute: (input) => { input.value = input.value.replace(/^\/tableflip\s*/, '') + ' (╯°□°)╯︵ ┻━┻'; } },
  { name: '/unflip', desc: 'Appends ┬─┬ ノ( ゜-゜ノ)', execute: (input) => { input.value = input.value.replace(/^\/unflip\s*/, '') + ' ┬─┬ ノ( ゜-゜ノ)'; } },
  { name: '/me', desc: 'Sends an action message (*your text*)', execute: (input) => { input.value = input.value.replace(/^\/me\s+/, '*') + '*'; } },
  { name: '/spoiler', desc: 'Wraps your message in spoiler tags', execute: (input) => { input.value = '||' + input.value.replace(/^\/spoiler\s+/, '') + '||'; } },
  { name: '/lenny', desc: 'Appends ( ͡° ͜ʖ ͡°)', execute: (input) => { input.value = input.value.replace(/^\/lenny\s*/, '') + ' ( ͡° ͜ʖ ͡°)'; } },
  { name: '/code', desc: 'Wraps your message in a code block', execute: (input) => { input.value = '```\n' + input.value.replace(/^\/code\s+/, '') + '\n```'; } },
  { name: '/quote', desc: 'Prepends > to your message (block quote)', execute: (input) => { input.value = '> ' + input.value.replace(/^\/quote\s+/, ''); } },
  { name: '/bold', desc: 'Wraps your message in **bold**', execute: (input) => { input.value = '**' + input.value.replace(/^\/bold\s+/, '') + '**'; } },
  { name: '/italic', desc: 'Wraps your message in *italics*', execute: (input) => { input.value = '*' + input.value.replace(/^\/italic\s+/, '') + '*'; } },
  { name: '/think', desc: 'Appends 🤔', execute: (input) => { input.value = input.value.replace(/^\/think\s*/, '') + ' \u{1F914}'; } },
  { name: '/clap', desc: 'Replaces spaces with 👏 (emphasis effect)', execute: (input) => { input.value = input.value.replace(/^\/clap\s+/, '').split(' ').join(' \u{1F44F} '); } },
  { name: '/fliptable', desc: 'Same as /tableflip', execute: (input) => { input.value = input.value.replace(/^\/fliptable\s*/, '') + ' (╯°□°)╯︵ ┻━┻'; } },
  { name: '/help', desc: 'Show all slash commands', execute: (input) => { input.value = '/help'; showToast('Type / to see all commands as you go.', 'ok'); } },
];

let slashPopup: HTMLElement | null = null;
let slashSelectedIdx = 0;
let slashMatches: SlashCommand[] = [];

function matchSlashCommands(text: string): SlashCommand[] {
  if (!text.startsWith('/') || text.includes(' ')) return [];
  const query = text.toLowerCase();
  return SLASH_COMMANDS.filter(c => c.name.startsWith(query));
}

function ensureSlashSelectedVisible(): void {
  if (!slashPopup) return;
  const active = slashPopup.querySelector<HTMLElement>('.slash-item-active');
  if (active) active.scrollIntoView({ block: 'nearest' });
}

function renderSlashMatches(input: HTMLTextAreaElement): void {
  if (!slashPopup) return;
  const listId = 'slash-popup-list';
  slashPopup.innerHTML = `
    <div class="slash-popup-title" id="slash-popup-title">Commands</div>
    <div class="slash-popup-list" id="${listId}" role="listbox" aria-labelledby="slash-popup-title">
      ${slashMatches.map((c, i) => `
        <div class="slash-item ${i === slashSelectedIdx ? 'slash-item-active' : ''}"
             role="option" aria-selected="${i === slashSelectedIdx}"
             data-idx="${i}" id="slash-item-${i}">
          <span class="slash-item-name">${esc(c.name)}</span>
          <span class="slash-item-desc">${esc(c.desc)}</span>
        </div>
      `).join('')}
    </div>
    <div class="slash-popup-footer">
      <span><kbd class="slash-kbd">Tab</kbd><kbd class="slash-kbd">\u21B5</kbd> to select</span>
      <span><kbd class="slash-kbd">esc</kbd> to close</span>
    </div>
  `;

  slashPopup.querySelectorAll<HTMLElement>('.slash-item').forEach(el => {
    el.addEventListener('mousedown', (e) => {
      e.preventDefault();
      const idx = parseInt(el.dataset['idx'] ?? '0');
      const cmd = slashMatches[idx];
      if (cmd) {
        cmd.execute(input);
        hideSlashAutocomplete();
        input.focus();
      }
    });
    el.addEventListener('mouseenter', () => {
      const idx = parseInt(el.dataset['idx'] ?? '0');
      if (idx === slashSelectedIdx) return;
      slashSelectedIdx = idx;
      renderSlashMatches(input);
    });
  });
}

export function showSlashAutocomplete(input: HTMLTextAreaElement): void {
  slashMatches = matchSlashCommands(input.value);
  if (slashMatches.length === 0) {
    hideSlashAutocomplete();
    return;
  }

  slashSelectedIdx = Math.min(slashSelectedIdx, slashMatches.length - 1);

  if (!slashPopup) {
    slashPopup = document.createElement('div');
    slashPopup.className = 'slash-popup';
    slashPopup.setAttribute('role', 'dialog');
    slashPopup.setAttribute('aria-label', 'Slash commands');
    input.closest('.chat-compose')?.appendChild(slashPopup);
  }

  renderSlashMatches(input);
  slashPopup.style.display = 'block';
  ensureSlashSelectedVisible();
}

function hideSlashAutocomplete(): void {
  if (slashPopup) {
    slashPopup.style.display = 'none';
    slashSelectedIdx = 0;
    slashMatches = [];
  }
}

export function handleSlashKeydown(e: KeyboardEvent, input: HTMLTextAreaElement): boolean {
  if (!slashPopup || slashPopup.style.display === 'none') return false;
  if (slashMatches.length === 0) return false;

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    slashSelectedIdx = (slashSelectedIdx + 1) % slashMatches.length;
    renderSlashMatches(input);
    ensureSlashSelectedVisible();
    return true;
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    slashSelectedIdx = (slashSelectedIdx - 1 + slashMatches.length) % slashMatches.length;
    renderSlashMatches(input);
    ensureSlashSelectedVisible();
    return true;
  }
  if (e.key === 'Home') {
    e.preventDefault();
    slashSelectedIdx = 0;
    renderSlashMatches(input);
    ensureSlashSelectedVisible();
    return true;
  }
  if (e.key === 'End') {
    e.preventDefault();
    slashSelectedIdx = slashMatches.length - 1;
    renderSlashMatches(input);
    ensureSlashSelectedVisible();
    return true;
  }
  if (e.key === 'Escape') {
    e.preventDefault();
    hideSlashAutocomplete();
    return true;
  }
  if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
    e.preventDefault();
    const cmd = slashMatches[slashSelectedIdx];
    if (cmd) {
      cmd.execute(input);
      hideSlashAutocomplete();
      input.focus();
    }
    return true;
  }
  return false;
}

/* ── Channel Mention Autocomplete (#channel) ── */

let channelPopup: HTMLElement | null = null;
let channelQuery = '';
let channelStart = -1;

export function showChannelAutocomplete(input: HTMLTextAreaElement): void {
  const text = input.value;
  const pos = input.selectionStart ?? text.length;

  // Find # before cursor
  let hashIdx = -1;
  for (let i = pos - 1; i >= 0; i--) {
    if (text[i] === '#') { hashIdx = i; break; }
    if (text[i] === ' ' && i < pos - 1) break;
  }

  if (hashIdx === -1) {
    hideChannelAutocomplete();
    return;
  }

  channelStart = hashIdx;
  channelQuery = text.substring(hashIdx + 1, pos).toLowerCase();

  const channels = state.channels
    .filter(c => c.server_id === state.currentServer && c.name.toLowerCase().includes(channelQuery))
    .slice(0, 8);

  if (channels.length === 0) {
    hideChannelAutocomplete();
    return;
  }

  if (!channelPopup) {
    channelPopup = document.createElement('div');
    channelPopup.className = 'channel-popup';
    (input.closest('.chat-compose') ?? input.parentElement)?.appendChild(channelPopup);
  }

  // Per-kind glyphs match Discord: # for text, speaker for voice, camera for video.
  // Falls back to # so unknown kinds still render something sensible.
  const kindIcon = (kind: string | undefined): string => {
    if (kind === 'voice') {
      return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/></svg>';
    }
    if (kind === 'video') {
      return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>';
    }
    return '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>';
  };
  channelPopup.innerHTML = channels.map(c => `
    <div class="channel-item" data-name="${esc(c.name)}">
      <span class="channel-item-icon">${kindIcon(c.channel_type)}</span>
      <span class="channel-item-name">${esc(c.name)}</span>
    </div>
  `).join('');
  channelPopup.style.display = 'block';

  channelPopup.querySelectorAll<HTMLElement>('.channel-item').forEach(el => {
    el.addEventListener('mousedown', (e) => {
      e.preventDefault();
      const name = el.dataset['name'] ?? '';
      const before = text.substring(0, channelStart);
      const after = text.substring(pos);
      input.value = `${before}#${name} ${after}`;
      input.focus();
      hideChannelAutocomplete();
    });
  });
}

function hideChannelAutocomplete(): void {
  if (channelPopup) {
    channelPopup.style.display = 'none';
  }
}

/* ── Emoji shortcode autocomplete (:name) ── */

const EMOJI_SHORTCODES: Record<string, string> = {
  smile: '😄', smiley: '😃', laughing: '😆', joy: '😂', rofl: '🤣',
  grin: '😁', wink: '😉', blush: '😊', heart_eyes: '😍', kissing: '😘',
  thinking: '🤔', sunglasses: '😎', tada: '🎉', fire: '🔥', sparkles: '✨',
  rocket: '🚀', star: '⭐', heart: '❤️', '+1': '👍', thumbsup: '👍',
  '-1': '👎', thumbsdown: '👎', clap: '👏', pray: '🙏', muscle: '💪',
  eyes: '👀', cry: '😢', sob: '😭', skull: '💀', poop: '💩',
  warning: '⚠️', white_check_mark: '✅', x: '❌', '100': '💯', ok: '🆗',
  bug: '🐛', bulb: '💡', wave: '👋', hand: '✋', point_up: '👆',
  shrug: '🤷', salute: '🫡', cool: '😎', party: '🥳', love: '🥰',
};

let emojiPopup: HTMLElement | null = null;
let emojiStart = -1;
let emojiQuery = '';
let emojiMatches: { code: string; glyph: string }[] = [];
let emojiSelectedIdx = 0;

export function showEmojiAutocomplete(input: HTMLTextAreaElement): void {
  const text = input.value;
  const pos = input.selectionStart ?? text.length;

  let colonIdx = -1;
  for (let i = pos - 1; i >= 0; i--) {
    if (text[i] === ':') { colonIdx = i; break; }
    if (text[i] === ' ' || text[i] === '\n') break;
    if (!/[a-z0-9_+\-]/i.test(text[i] ?? '')) break;
  }
  if (colonIdx === -1) { hideEmojiAutocomplete(); return; }

  emojiStart = colonIdx;
  emojiQuery = text.substring(colonIdx + 1, pos).toLowerCase();
  if (emojiQuery.length === 0) { hideEmojiAutocomplete(); return; }

  emojiMatches = Object.entries(EMOJI_SHORTCODES)
    .filter(([code]) => code.startsWith(emojiQuery))
    .slice(0, 8)
    .map(([code, glyph]) => ({ code, glyph }));
  emojiSelectedIdx = 0;
  if (emojiMatches.length === 0) { hideEmojiAutocomplete(); return; }

  if (!emojiPopup) {
    emojiPopup = document.createElement('div');
    emojiPopup.className = 'emoji-popup';
    (input.closest('.chat-compose') ?? input.parentElement)?.appendChild(emojiPopup);
  }
  renderEmojiAutocomplete(input);
  emojiPopup.style.display = 'block';
}

function renderEmojiAutocomplete(input: HTMLTextAreaElement): void {
  if (!emojiPopup) return;
  emojiPopup.innerHTML = emojiMatches.map((m, i) => `
    <div class="emoji-popup-item ${i === emojiSelectedIdx ? 'active' : ''}" data-idx="${i}">
      <span class="emoji-popup-glyph">${m.glyph}</span>
      <span class="emoji-popup-code">:${esc(m.code)}:</span>
    </div>
  `).join('');
  emojiPopup.querySelectorAll<HTMLElement>('.emoji-popup-item').forEach(el => {
    el.addEventListener('mousedown', (e) => {
      e.preventDefault();
      const idx = parseInt(el.dataset['idx'] ?? '0');
      selectEmojiAutocomplete(input, idx);
    });
  });
}

function selectEmojiAutocomplete(input: HTMLTextAreaElement, idx: number): void {
  const m = emojiMatches[idx];
  if (!m) return;
  const text = input.value;
  const pos = input.selectionStart ?? text.length;
  const before = text.substring(0, emojiStart);
  const after = text.substring(pos);
  input.value = `${before}${m.glyph} ${after}`;
  const cursor = before.length + m.glyph.length + 1;
  input.selectionStart = input.selectionEnd = cursor;
  input.focus();
  hideEmojiAutocomplete();
}

export function handleEmojiKeydown(e: KeyboardEvent, input: HTMLTextAreaElement): boolean {
  if (!emojiPopup || emojiPopup.style.display === 'none' || emojiMatches.length === 0) return false;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    emojiSelectedIdx = (emojiSelectedIdx + 1) % emojiMatches.length;
    renderEmojiAutocomplete(input);
    return true;
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    emojiSelectedIdx = (emojiSelectedIdx - 1 + emojiMatches.length) % emojiMatches.length;
    renderEmojiAutocomplete(input);
    return true;
  }
  if (e.key === 'Enter' || e.key === 'Tab') {
    e.preventDefault();
    selectEmojiAutocomplete(input, emojiSelectedIdx);
    return true;
  }
  if (e.key === 'Escape') {
    e.preventDefault();
    hideEmojiAutocomplete();
    return true;
  }
  return false;
}

function hideEmojiAutocomplete(): void {
  if (emojiPopup) {
    emojiPopup.style.display = 'none';
  }
}

/** Get person name by ID — falls back to "Member-<prefix>" instead of raw UUID. */
function authorName(id: string): string {
  return state.people.find(p => p.id === id)?.name || `Member-${id.substring(0, 6)}`;
}

/** Get badge for a person (owner crown, admin shield) */
function authorBadge(id: string): string {
  const server = state.servers.find(s => s.id === state.currentServer);
  if (server?.owner_id === id) {
    return '<span class="msg-badge msg-badge-owner" title="Server Owner"><svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/></svg></span>';
  }
  const member = state.serverMembers.find(m => m.server_id === state.currentServer && m.person_id === id);
  if (member?.role === 'admin') {
    return '<span class="msg-badge msg-badge-admin" title="Admin"><svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg></span>';
  }
  return '';
}

/** Highest-role color for a person in the current server. Empty string if the
 *  person has no colored role — caller falls back to default text color. */
function authorColor(id: string): string {
  return state.roleColors[id] ?? '';
}

/** Get initial for avatar */
function authorInitial(id: string): string {
  const name = authorName(id);
  return name.charAt(0).toUpperCase();
}

/** Should this message show full header (avatar+name) or compact? */
function shouldShowHeader(msg: Message, prevMsg: Message | null): boolean {
  if (!prevMsg) return true;
  if (prevMsg.author_id !== msg.author_id) return true;
  // Collapse if same author within 5 minutes (Discord behavior)
  const diff = new Date(msg.created_at).getTime() - new Date(prevMsg.created_at).getTime();
  return diff > 5 * 60 * 1000;
}

function isOwnMessageForCurrentUser(msg: Message): boolean {
  const myId = state.ownerId || state.people[0]?.id || '';
  return !!myId && msg.author_id === myId;
}

function readIcon(read: boolean): string {
  return read
    ? '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/><polyline points="16 6 8 14 4 10"/></svg>'
    : '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
}

/** Render a single message */
function renderMessage(msg: Message, showHeader: boolean): string {
  // System messages — Discord parity: centered "joined", "pinned a message",
  // "started a thread" rows. No avatar, no quick-bar, faint icon.
  if (msg.kind === 'system') {
    return renderSystemMessage(msg);
  }
  const name = authorName(msg.author_id);
  const initial = authorInitial(msg.author_id);
  const color = authorColor(msg.author_id);
  const nameStyle = color ? ` style="color:${color}"` : '';
  const badge = authorBadge(msg.author_id);
  const time = formatTime(msg.created_at);
  const editedTs = msg.edited_at ? esc(fmtFull(msg.edited_at)) : '';
  const edited = msg.edited_at
    ? ` <span class="msg-edited" title="Edited ${editedTs}">(edited)</span>`
    : '';
  const content = msg.content ? renderMarkdown(msg.content) : '';
  const namedGuestReceipt = isOwnMessageForCurrentUser(msg)
    ? `<span class="msg-read-receipt" title="${msg.read_by_named_guest ? 'Read' : 'Sent'}">${msg.read_by_named_guest ? readIcon(true) : readIcon(false)}</span>`
    : '';

  // Render attachments
  let attachHtml = '';
  try {
    const attachments: Attachment[] = JSON.parse(msg.attachments || '[]');
    for (const a of attachments) {
      const dlUrl = `${API_BASE}/api/downloads/${encodeURIComponent(a.id)}?person_id=${encodeURIComponent(state.ownerId || '')}&server_id=${encodeURIComponent(state.currentServer || '')}&filename=${encodeURIComponent(a.filename)}`;
      const dlBtn = `<button class="msg-dl-btn" data-dl-url="${esc(dlUrl)}" title="Download ${esc(a.filename)}"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></button>`;
      if (a.content_type?.startsWith('image/')) {
        attachHtml += `<div class="msg-attachment msg-attachment-image">
          <img src="${esc(API_BASE + a.url)}" alt="${esc(a.filename)}" loading="lazy"
            class="msg-img-preview" data-fullurl="${esc(API_BASE + a.url)}"
            style="max-width:400px;max-height:300px;border-radius:8px;cursor:pointer">
          ${dlBtn}
        </div>`;
      } else if (a.content_type?.startsWith('video/')) {
        attachHtml += `<div class="msg-attachment">
          <video src="${esc(API_BASE + a.url)}" controls style="max-width:400px;border-radius:8px"></video>
          ${dlBtn}
        </div>`;
      } else if (a.content_type?.startsWith('audio/')) {
        attachHtml += `<div class="msg-attachment">
          <audio src="${esc(API_BASE + a.url)}" controls></audio>
          ${dlBtn}
        </div>`;
      } else {
        const sizeStr = a.size < 1024 ? `${a.size}B` : a.size < 1048576 ? `${(a.size/1024).toFixed(1)}KB` : `${(a.size/1048576).toFixed(1)}MB`;
        // Clicking the filename opens/views the file in a new tab. PDFs and
        // .md/.txt render inline (the backend serves them with viewable
        // content-types + Content-Disposition: inline); other types fall back
        // to the browser's default handler. The download button (dlBtn) stays
        // for saving straight to disk.
        const viewUrl = esc(API_BASE + a.url);
        attachHtml += `<div class="msg-attachment msg-attachment-file">
          <a class="msg-file-link" href="${viewUrl}" target="_blank" rel="noopener noreferrer" title="Open ${esc(a.filename)} in a new tab">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" style="opacity:0.6"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>
            <span class="msg-file-name">${esc(a.filename)}</span>
            <span class="msg-file-size">${sizeStr}</span>
          </a>
          ${dlBtn}
        </div>`;
      }
    }
  } catch { /* invalid attachments JSON */ }

  // Render reactions under message. Accept BOTH backend shapes:
  //   array  → [{ emoji, count, me?, users? }, ...]   (current backend)
  //   record → { ":smile:": 2 }                       (legacy)
  let reactionsHtml = '';
  try {
    const raw = msg.reactions;
    if (raw && raw !== '[]' && raw !== '{}') {
      const parsed = JSON.parse(raw);
      let pairs: Array<{ emoji: string; count: number; me?: boolean }> = [];
      if (Array.isArray(parsed)) {
        pairs = parsed
          .filter((r): r is { emoji: string; count: number; me?: boolean } =>
            r && typeof r === 'object' && typeof r.emoji === 'string' && typeof r.count === 'number')
          .map(r => ({ emoji: r.emoji, count: r.count, me: !!r.me }));
      } else if (parsed && typeof parsed === 'object') {
        pairs = Object.entries(parsed as Record<string, unknown>)
          .filter(([, v]) => typeof v === 'number' && (v as number) > 0)
          .map(([emoji, count]) => ({ emoji, count: count as number }));
      }
      if (pairs.length > 0) {
        reactionsHtml = '<div class="msg-reactions" role="group" aria-label="Reactions">' + pairs.map(p => {
          const act = p.me ? 'Remove' : 'Add';
          const lbl = `${act} reaction ${p.emoji}, ${p.count} ${p.count === 1 ? 'person' : 'people'} reacted`;
          return `<button class="msg-reaction-chip${p.me ? ' msg-reaction-chip-mine' : ''}" data-emoji="${esc(p.emoji)}" data-msg="${esc(msg.id)}" type="button" aria-pressed="${p.me ? 'true' : 'false'}" aria-label="${esc(lbl)}" title="${esc(lbl)}"><span class="msg-reaction-emoji">${esc(p.emoji)}</span><span class="msg-reaction-count">${p.count}</span></button>`;
        }).join('') + `<button class="msg-reaction-add" data-msg="${esc(msg.id)}" type="button" aria-label="Add reaction" title="Add reaction"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/></svg></button>` + '</div>';
      }
    }
  } catch { /* invalid reactions JSON */ }

  // Reply preview — Discord-style left-bar accent with inline avatar
  let replyHtml = '';
  if (msg.reply_to) {
    const parent = messageCache.get(msg.reply_to);
    if (parent) {
      const parentName = authorName(parent.author_id);
      const parentInitial = authorInitial(parent.author_id);
      const parentColor = authorColor(parent.author_id);
      const parentStyle = parentColor ? ` style="color:${parentColor}"` : '';
      const parentSnippet = parent.content.length > 80 ? parent.content.substring(0, 80) + '…' : parent.content;
      replyHtml = `<div class="msg-reply-ref" data-jump="${esc(msg.reply_to)}" title="Jump to message">
        <span class="msg-reply-arrow">${iconReplyArrow}</span>
        <span class="msg-reply-avatar" style="background:${avatarBg(parent.author_id)}">${esc(parentInitial)}</span>
        <span class="msg-reply-author"${parentStyle}>@${esc(parentName)}</span>
        <span class="msg-reply-text">${esc(parentSnippet) || '<em class="msg-reply-empty">(attachment)</em>'}</span>
      </div>`;
    } else {
      replyHtml = `<div class="msg-reply-ref"><span class="msg-reply-arrow">${iconReplyArrow}</span><span class="msg-reply-text" style="opacity:0.4">Original message unavailable</span></div>`;
    }
  }

  const myId = state.ownerId || state.people[0]?.id || '';
  const isOwn = msg.author_id === myId;
  // Discord-style hover quick-bar — top-right float, fades in 120ms on hover.
  const pinTitle = msg.pinned ? 'Unpin' : 'Pin';
  const pinActive = msg.pinned ? ' msg-quick-active' : '';
  const actions = `<div class="msg-quick-bar msg-quickbar msg-actions" data-msg-actions role="toolbar" aria-label="Message actions">
    <button class="msg-quick-btn" data-action="react" data-msg="${esc(msg.id)}" title="Add Reaction" aria-label="Add Reaction">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
    </button>
    <button class="msg-quick-btn" data-action="reply" data-msg="${esc(msg.id)}" title="Reply (R)" aria-label="Reply">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>
    </button>
    <button class="msg-quick-btn${pinActive}" data-action="pin" data-msg="${esc(msg.id)}" data-pinned="${msg.pinned}" title="${pinTitle}" aria-label="${pinTitle}">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24z"/></svg>
    </button>
    <button class="msg-quick-btn" data-action="thread" data-msg="${esc(msg.id)}" title="Start Thread" aria-label="Start Thread">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
    </button>
    <button class="msg-quick-btn" data-action="forward" data-msg="${esc(msg.id)}" title="Forward" aria-label="Forward">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 17 20 12 15 7"/><path d="M4 18v-2a4 4 0 0 1 4-4h12"/></svg>
    </button>
    ${isOwn ? `<button class="msg-quick-btn" data-action="edit" data-msg="${esc(msg.id)}" title="Edit" aria-label="Edit">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
    </button>` : ''}
    <button class="msg-quick-btn" data-action="more" data-msg="${esc(msg.id)}" title="More" aria-label="More">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
    </button>
  </div>`;

  if (showHeader) {
    return `<div class="msg" data-id="${esc(msg.id)}" data-author="${esc(msg.author_id)}">
      ${replyHtml}
      <div class="msg-avatar" style="background:${avatarBg(msg.author_id)}">${esc(initial)}</div>
      <div class="msg-body">
        <div class="msg-header">
          <span class="msg-author"${nameStyle}>${esc(name)}</span>${badge}
          <span class="msg-time" title="${esc(fmtFull(msg.created_at))}">${esc(time)}</span>
          ${namedGuestReceipt}
        </div>
        <div class="msg-content">${content}${edited}</div>
        ${attachHtml}
        ${reactionsHtml}
      </div>
      ${actions}
    </div>`;
  }

  return `<div class="msg msg-compact" data-id="${esc(msg.id)}" data-author="${esc(msg.author_id)}">
    ${replyHtml}
    <div class="msg-compact-time" title="${esc(fmtFull(msg.created_at))}">${esc(time.split(' at ').pop() || time)}</div>
    <div class="msg-body">
      <div class="msg-content">${content}${edited}${namedGuestReceipt}</div>
      ${attachHtml}
      ${reactionsHtml}
    </div>
    ${actions}
  </div>`;
}

/** Discord-parity system message — centered single-line row with a faint
 *  glyph. Used for member-joined/left/pinned/thread-created events when the
 *  backend marks a row with kind === 'system'. */
function renderSystemMessage(msg: Message): string {
  const ev = (msg.system_event || '').toLowerCase();
  const name = authorName(msg.author_id);
  const time = formatTime(msg.created_at);
  const fullTs = fmtFull(msg.created_at);
  let icon = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="3"/></svg>';
  let body = msg.content || 'Activity';
  if (ev === 'joined' || ev.includes('join')) {
    icon = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>';
    body = `${esc(name)} joined the channel.`;
  } else if (ev === 'left' || ev.includes('leave')) {
    icon = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="17" y1="8" x2="22" y2="13"/><line x1="22" y1="8" x2="17" y2="13"/></svg>';
    body = `${esc(name)} left the channel.`;
  } else if (ev === 'pinned' || ev.includes('pin')) {
    icon = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/></svg>';
    body = `${esc(name)} pinned a message.`;
  } else if (ev.includes('thread')) {
    icon = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>';
    body = `${esc(name)} started a thread.`;
  } else {
    body = esc(msg.content || `${name} did something.`);
  }
  return `<div class="msg msg-system" data-id="${esc(msg.id)}" role="status">
    <span class="msg-system-icon" aria-hidden="true">${icon}</span>
    <span class="msg-system-body">${body}</span>
    <span class="msg-system-time" title="${esc(fullTs)}">${esc(time)}</span>
  </div>`;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;
let lastMessageId = '';
let pollFailCount = 0;
let replyKeyHandler: ((e: KeyboardEvent) => void) | null = null;

/** Active realtime listener disposers for the currently rendered channel. */
let realtimeUnsubs: Array<() => void> = [];
let activeChannelTopic = '';

/** Per-channel scroll memory — persist position when jumping between channels */
const channelScrollPositions = new Map<string, number>();
let currentChannelKey = '';

/* ── Message list virtualization ──
 * Keeps at most VIRT_WINDOW messages rendered in the DOM. Messages scrolled
 * far out of view get replaced with empty placeholder divs that preserve
 * layout height so scroll position doesn't jump.
 */
const VIRT_WINDOW = 50;
let virtObserver: IntersectionObserver | null = null;
/** Map of message id → { html, height } for messages that have been unmounted */
const virtStash = new Map<string, { html: string; height: number }>();

function setupVirtualization(container: HTMLElement): void {
  teardownVirtualization();
  // Only activate if there are enough messages to benefit
  const msgs = container.querySelectorAll<HTMLElement>('.msg');
  if (msgs.length <= VIRT_WINDOW) return;

  virtObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const el = entry.target as HTMLElement;
      const id = el.getAttribute('data-id');
      if (!id) continue;

      if (!entry.isIntersecting && !el.classList.contains('virt-placeholder')) {
        // Unmount: stash HTML and replace with height-preserving placeholder
        const rect = el.getBoundingClientRect();
        virtStash.set(id, { html: el.outerHTML, height: rect.height });
        const ph = document.createElement('div');
        ph.className = 'virt-placeholder';
        ph.setAttribute('data-id', id);
        ph.style.height = `${rect.height}px`;
        el.replaceWith(ph);
        virtObserver?.observe(ph);
      } else if (entry.isIntersecting && el.classList.contains('virt-placeholder')) {
        // Remount: restore stashed HTML
        const stashed = virtStash.get(id);
        if (stashed) {
          const wrapper = document.createElement('div');
          wrapper.innerHTML = stashed.html;
          const restored = wrapper.firstElementChild as HTMLElement;
          if (restored) {
            el.replaceWith(restored);
            virtObserver?.observe(restored);
            virtStash.delete(id);
          }
        }
      }
    }
  }, {
    root: container,
    rootMargin: '200px 0px',
  });

  msgs.forEach(msg => virtObserver!.observe(msg));
}

function teardownVirtualization(): void {
  if (virtObserver) {
    virtObserver.disconnect();
    virtObserver = null;
  }
  virtStash.clear();
}

/** Ensure a message is rendered (not a placeholder) so we can scroll to it */
function ensureMessageRendered(container: HTMLElement, msgId: string): HTMLElement | null {
  let el = container.querySelector<HTMLElement>(`[data-id="${msgId}"]`);
  if (!el) return null;
  if (el.classList.contains('virt-placeholder')) {
    const stashed = virtStash.get(msgId);
    if (stashed) {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = stashed.html;
      const restored = wrapper.firstElementChild as HTMLElement;
      if (restored) {
        el.replaceWith(restored);
        virtObserver?.observe(restored);
        virtStash.delete(msgId);
        el = restored;
      }
    }
  }
  return el;
}

/** Render the chat view for a channel */
export function renderChat(body: HTMLElement, channel: string): void {
  const sid = state.currentServer;
  if (!sid) return;

  // Stop previous poll
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }

  // Advance the unread marker for the *previous* channel iff we're actually
  // switching away from it. switchServer can re-render the same channel
  // multiple times in a single tick (sync state mutation + the async
  // loadChannelsForServer.then re-render), and each re-render previously
  // wrote lastRead for the channel we were re-entering — wiping the seed
  // and breaking the unread divider on the very next visit. Gate by key
  // change so the same-channel re-render is a no-op.
  const newKey = `${sid}:${channel}`;
  if (currentChannelKey && currentChannelKey !== newKey) {
    let latestIso = '';
    messageCache.forEach(m => { if (m.created_at > latestIso) latestIso = m.created_at; });
    if (latestIso) localStorage.setItem(`hub-people-lastread:${currentChannelKey}`, latestIso);
  }
  currentChannelKey = newKey;

  body.innerHTML = `
    <div class="chat-container">
      <div class="chat-messages" id="chat-messages" role="log" aria-live="polite" aria-relevant="additions" aria-label="Channel messages">
        <div class="chat-skeleton">
          ${[1,2,3,4,5].map(() => `<div class="skel-msg">
            <div class="skel-avatar"></div>
            <div class="skel-body">
              <div class="skel-line skel-name"></div>
              <div class="skel-line skel-text"></div>
              <div class="skel-line skel-text-short"></div>
            </div>
          </div>`).join('')}
        </div>
      </div>
      <div class="chat-typing" id="chat-typing"></div>
      <div class="chat-compose-mount" id="chat-compose-mount"></div>
      <button class="chat-scroll-btn" id="chat-scroll-btn" style="display:none" title="Jump to latest">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/></svg>
      </button>
      <div class="chat-drag-overlay" id="chat-drag-overlay" style="display:none">
        <div class="chat-drag-text">Drop to upload</div>
      </div>
    </div>
  `;

  const messagesEl = document.getElementById('chat-messages') as HTMLElement;
  const typingEl = document.getElementById('chat-typing') as HTMLElement;
  const composeMount = document.getElementById('chat-compose-mount') as HTMLElement;

  // Mount the shared compose component. Every call to renderChat tears the
  // previous one down so switching channels doesn't leak listeners or
  // double-bind the same textarea.
  composeHandle?.destroy();
  composeHandle = mountMessageCompose(composeMount, {
    draftKey: `ch:${sid}:${channel}`,
    placeholder: `Message #${channel}`,
    canSend: (text) => text.length > 0 || pendingFiles.length > 0,
    onSend: (_raw, sendOpts) => {
      void sendMessage(sid, channel, messagesEl, sendOpts);
    },
    onSilentSend: () => { showToast('Silent send coming soon', 'ok'); },
    onInput: (textarea) => {
      // @mention / slash / channel / emoji autocompletes read off the current
      // caret context and render their own popovers anchored to the textarea.
      showMentionAutocomplete(textarea);
      showSlashAutocomplete(textarea);
      showChannelAutocomplete(textarea);
      showEmojiAutocomplete(textarea);
    },
    onKeydown: (e, textarea) => {
      if (handleMentionKeydown(e, textarea)) return true;
      if (handleEmojiKeydown(e, textarea)) return true;
      if (handleSlashKeydown(e, textarea)) return true;
      // Discord parity: ArrowUp on empty compose edits the last own message.
      if (e.key === 'ArrowUp' && textarea.value === ''
          && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const myId = state.ownerId || state.people[0]?.id || '';
        if (!myId) return false;
        const ownIds: string[] = [];
        messageCache.forEach((m) => { if (m.author_id === myId) ownIds.push(m.id); });
        if (!ownIds.length) return false;
        ownIds.sort((a, b) => {
          const ta = messageCache.get(a)?.created_at ?? '';
          const tb = messageCache.get(b)?.created_at ?? '';
          return ta < tb ? -1 : ta > tb ? 1 : 0;
        });
        const lastId = ownIds[ownIds.length - 1];
        if (!lastId) return false;
        e.preventDefault();
        startInlineEdit(lastId, messagesEl);
        return true;
      }
      return false;
    },
    onFilesAdded: (files) => {
      for (const f of files) pendingFiles.push(f);
      const preview = composeHandle?.attachPreview;
      if (preview) renderAttachPreview(preview);
    },
  });

  const inputEl = composeHandle.textarea;

  // Auto-focus the compose on desktop when a channel opens. Typing "starts"
  // immediately without a click, which is the biggest single usability fix
  // for the compose on a laptop/desktop. On mobile we deliberately do NOT
  // focus — auto-popping the on-screen keyboard every time you switch
  // channels is annoying and steals screen real-estate.
  if (!window.matchMedia('(pointer: coarse)').matches) {
    // Defer by a tick so the DOM is fully attached before focus() — avoids
    // browsers silently ignoring focus on an element still mid-insert.
    setTimeout(() => { try { composeHandle?.focus(); } catch { /* noop */ } }, 0);
  }

  // Mobile soft-keyboard compose lift is handled entirely in CSS now
  // (.chat-compose two-layer margin-bottom: var(--keyboard-offset) for iOS +
  // env(keyboard-inset-height) for Chromium — see channels.css). No per-view
  // visualViewport JS: on Chromium overlaysContent=true keeps vv.height full,
  // so the old innerHeight - vv.height math no-op'd and left the bar behind the
  // keyboard. The global initKeepFocusAboveKeyboard() in app.ts covers scroll.

  // Swipe-down on the message list while the compose textarea is focused
  // blurs it — Android Chrome won't always dismiss the keyboard via
  // momentum scroll, so a deliberate 80px drag down kicks focus off the
  // textarea and the OS closes the keyboard.
  if (window.matchMedia('(pointer: coarse)').matches) {
    let kbSwipeStartY = 0;
    let kbSwipeTracking = false;
    messagesEl.addEventListener('touchstart', (e: TouchEvent) => {
      kbSwipeStartY = e.touches[0]?.clientY ?? 0;
      kbSwipeTracking = document.activeElement === inputEl;
    }, { passive: true });
    messagesEl.addEventListener('touchmove', (e: TouchEvent) => {
      if (!kbSwipeTracking) return;
      const dy = (e.touches[0]?.clientY ?? 0) - kbSwipeStartY;
      if (dy > 80) {
        inputEl.blur();
        kbSwipeTracking = false;
      }
    }, { passive: true });
  }

  // Load messages
  loadMessages(sid, channel, messagesEl);

  // Read-only channel gate: async check, tears down compose if user lacks required role
  void canComposeInChannel(channel, sid).then(allowed => {
    if (!allowed && composeMount) {
      composeHandle?.destroy();
      composeHandle = null;
      composeMount.innerHTML = `<div class="chat-readonly-bar">This channel is read-only. Only members with an <strong>AI</strong> or <strong>Dev</strong> role can post here.</div>`;
    }
  });

  // Blocked-user filter \u2014 hide any message rows from authors in the local
  // block list (managed via the profile card "Block messages from this user"
  // toggle). Runs after every render via MutationObserver so newly-arrived
  // realtime messages are also hidden without a full re-render.
  try {
    const blockedRaw = localStorage.getItem('hub-people-blocked') || '[]';
    const blocked = new Set<string>(JSON.parse(blockedRaw) as string[]);
    if (blocked.size > 0) {
      const hideBlocked = (): void => {
        messagesEl.querySelectorAll<HTMLElement>('.msg').forEach(el => {
          const a = el.getAttribute('data-author');
          if (a && blocked.has(a)) el.style.display = 'none';
        });
      };
      hideBlocked();
      const mo = new MutationObserver(hideBlocked);
      mo.observe(messagesEl, { childList: true });
      // Disconnect on channel change to avoid orphan observers piling up.
      const w = window as unknown as Record<string, unknown>;
      const prev = w['__hpCleanup'] as (() => void) | undefined;
      w['__hpCleanup'] = () => { mo.disconnect(); prev?.(); };
    }
  } catch { /* malformed JSON \u2014 ignore */ }

  // Typing indicator — debounced 3s. Push via realtime when connected, fall
  // back to REST so the `/api/messages/typing` endpoint still updates server
  // state when the WS is dropped.
  let typingTimeout: ReturnType<typeof setTimeout> | null = null;
  inputEl.addEventListener('input', () => {
    if (!typingTimeout) {
      const myId = state.ownerId || state.people[0]?.id || '';
      if (realtime.getState() === 'open') {
        realtime.sendTyping(channelTopic(sid, channel), myId);
      } else {
        void post('/api/messages/typing', { server_id: sid, channel, person_id: myId }).catch(() => {});
      }
    }
    if (typingTimeout) clearTimeout(typingTimeout);
    typingTimeout = setTimeout(() => { typingTimeout = null; }, 3000);
  });

  // Escape hides every autocomplete popover; the reply bar is handled by the
  // compose component itself. Blur hides them too (after a short delay so
  // clicks inside the popover still land).
  inputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      hideMentionAutocomplete();
      hideSlashAutocomplete();
      hideChannelAutocomplete();
      hideEmojiAutocomplete();
    }
  });
  inputEl.addEventListener('blur', () => {
    setTimeout(() => {
      hideMentionAutocomplete();
      hideSlashAutocomplete();
      hideChannelAutocomplete();
    }, 200);
  });

  // ── Scroll-to-bottom button + "X new messages" pill (Discord parity) ──
  // The pill counts new messages that arrived while the user was scrolled up.
  // Resets when they scroll back to bottom OR when they tap the pill (which
  // also smooth-scrolls them to the latest). Anchored on top of the scroll
  // button so they share the same fixed-bottom-right position.
  const scrollBtn = document.getElementById('chat-scroll-btn');
  let unseenCount = 0;
  let isAtBottom = true;
  let pillEl: HTMLButtonElement | null = null;

  const ensurePill = (): HTMLButtonElement => {
    if (pillEl) return pillEl;
    pillEl = document.createElement('button');
    pillEl.type = 'button';
    pillEl.className = 'chat-newmsg-pill';
    pillEl.style.display = 'none';
    pillEl.addEventListener('click', () => {
      messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: 'smooth' });
    });
    messagesEl.parentElement?.appendChild(pillEl);
    return pillEl;
  };

  const updatePill = (): void => {
    const p = ensurePill();
    if (unseenCount > 0 && !isAtBottom) {
      p.textContent = `${unseenCount} new message${unseenCount === 1 ? '' : 's'} \u2193`;
      p.style.display = 'flex';
    } else {
      p.style.display = 'none';
    }
  };

  messagesEl.addEventListener('scroll', () => {
    isAtBottom = messagesEl.scrollHeight - messagesEl.scrollTop - messagesEl.clientHeight < 100;
    if (scrollBtn) scrollBtn.style.display = isAtBottom ? 'none' : 'flex';
    if (isAtBottom) { unseenCount = 0; updatePill(); }
  });
  scrollBtn?.addEventListener('click', () => {
    messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: 'smooth' });
    if (scrollBtn) scrollBtn.style.display = 'none';
    unseenCount = 0; updatePill();
  });

  // Hook for realtime: bump the unseen counter when a new message arrives
  // and the user isn't pinned to the bottom. Stash on window so the realtime
  // handler (which is a separate IIFE) can call it without a circular import.
  (window as unknown as Record<string, unknown>)['__hpBumpUnseen'] = (): void => {
    if (isAtBottom) return;
    unseenCount += 1; updatePill();
  };
  (window as unknown as Record<string, unknown>)['__hpIsAtBottom'] = (): boolean => isAtBottom;

  // ── R hotkey on hovered message starts reply ──
  let hoveredMessageId: string | null = null;
  messagesEl.addEventListener('mouseover', (e) => {
    const msgEl = (e.target as HTMLElement).closest('.msg') as HTMLElement | null;
    if (msgEl) hoveredMessageId = msgEl.getAttribute('data-id');
  });
  messagesEl.addEventListener('mouseleave', () => { hoveredMessageId = null; });
  if (replyKeyHandler) document.removeEventListener('keydown', replyKeyHandler);
  replyKeyHandler = (e: KeyboardEvent): void => {
    const ae = document.activeElement;
    const tag = (ae?.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || (ae as HTMLElement | null)?.isContentEditable) return;
    if (e.key === 'r' && hoveredMessageId && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const msg = messageCache.get(hoveredMessageId);
      if (msg) { e.preventDefault(); openReplyBarFor(msg); }
    }
  };
  document.addEventListener('keydown', replyKeyHandler);

  // ── Attachment preview container + drag-drop overlay ──
  const previewEl = composeHandle.attachPreview!;
  const container = body.querySelector('.chat-container') as HTMLElement;
  const dragOverlay = document.getElementById('chat-drag-overlay') as HTMLElement;

  pendingFiles = [];

  // Emoji / GIF / Sticker compose buttons. GIF + Sticker are stubs per spec
  // — real Giphy / sticker integrations deferred to a future batch. The
  // compose-mounted buttons are bound here so the picker still anchors to
  // the correct element.
  composeHandle.emojiBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    showComposeEmojiPicker(e.currentTarget as HTMLElement, inputEl);
  });
  composeHandle.gifBtn?.addEventListener('click', (e) => {
    void openGifPicker(e.currentTarget as HTMLElement, inputEl);
  });
  composeHandle.stickerBtn?.addEventListener('click', (e) => {
    void openStickerPicker(e.currentTarget as HTMLElement, inputEl);
  });

  // Paste images
  inputEl.addEventListener('paste', (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          pendingFiles.push(file);
          renderAttachPreview(previewEl);
        }
      }
    }
  });

  // Drag & drop
  container.addEventListener('dragover', (e) => {
    e.preventDefault();
    dragOverlay.style.display = 'flex';
  });
  container.addEventListener('dragleave', (e) => {
    if (e.target === dragOverlay || e.target === container) {
      dragOverlay.style.display = 'none';
    }
  });
  container.addEventListener('drop', (e) => {
    e.preventDefault();
    dragOverlay.style.display = 'none';
    if (e.dataTransfer?.files) {
      for (const f of Array.from(e.dataTransfer.files)) pendingFiles.push(f);
      renderAttachPreview(previewEl);
    }
  });

  // Download button — role-gated via /api/downloads/:id
  messagesEl.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('.msg-dl-btn') as HTMLElement | null;
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      const url = btn.dataset['dlUrl'];
      if (url) window.open(url, '_blank');
    }
  });

  // Image lightbox — click image to expand
  messagesEl.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('msg-img-preview')) {
      const url = target.dataset['fullurl'] || (target as HTMLImageElement).src;
      showLightbox(url);
    }
    // Profile popup on author name click
    if (target.classList.contains('msg-author')) {
      const msgEl = target.closest('.msg');
      const authorId = msgEl?.getAttribute('data-author');
      if (authorId) openProfileCard(authorId, target);
    }
    // Profile popup on avatar click (Discord parity)
    const avatarEl = target.closest('.msg-avatar') as HTMLElement | null;
    if (avatarEl) {
      const msgEl = avatarEl.closest('.msg');
      const authorId = msgEl?.getAttribute('data-author');
      if (authorId) openProfileCard(authorId, avatarEl);
    }
    // Click reaction chip to toggle (use closest so clicks on inner spans still hit)
    const chip = target.closest('.msg-reaction-chip') as HTMLElement | null;
    if (chip) {
      const emoji = chip.dataset['emoji'];
      const reactionMsgId = chip.dataset['msg'];
      if (emoji && reactionMsgId) {
        const personId = state.ownerId || state.people[0]?.id || '';
        // Optimistic bump animation on the count so the UX feels instant even
        // before the SSE/reaction_changed frame rewrites the DOM.
        const countEl = chip.querySelector('.msg-reaction-count');
        if (countEl) {
          countEl.classList.remove('msg-reaction-count-bump');
          // force reflow so re-adding the class restarts the keyframe
          void (countEl as HTMLElement).offsetWidth;
          countEl.classList.add('msg-reaction-count-bump');
        }
        void post('/api/reactions/add', { message_id: reactionMsgId, person_id: personId, emoji });
      }
    }
    // Click "+" quick-add-reaction button → open emoji picker
    const addBtn = target.closest('.msg-reaction-add') as HTMLElement | null;
    if (addBtn) {
      const reactionMsgId = addBtn.dataset['msg'];
      if (reactionMsgId) showEmojiPicker(addBtn, reactionMsgId, messagesEl);
    }
    // Click mention to show profile
    if (target.classList.contains('mention') && target.dataset['person']) {
      openProfileCard(target.dataset['person'], target);
    }
    // Click #channel mention to navigate
    if (target.classList.contains('mention-channel') && target.dataset['channel']) {
      state.currentChannel = target.dataset['channel'];
      void import('../app').then(({ renderApp }) => renderApp());
    }
    // Jump to reply source (remounts virtualised placeholder if needed)
    if (target.closest('.msg-reply-ref')) {
      const jumpTo = target.closest('.msg-reply-ref')?.getAttribute('data-jump');
      if (jumpTo) {
        const msgEl = ensureMessageRendered(messagesEl, jumpTo);
        if (msgEl) {
          msgEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          msgEl.classList.add('msg-highlight');
          setTimeout(() => msgEl.classList.remove('msg-highlight'), 2000);
        }
      }
    }
    // Discord-parity code-block copy button — grabs raw source off the wrapper
    // so we don't have to decode the rendered HTML (which may contain spans).
    const copyBtn = target.closest('.msg-codeblock-copy') as HTMLElement | null;
    if (copyBtn) {
      const wrap = copyBtn.closest('.msg-codeblock-wrap') as HTMLElement | null;
      const raw = wrap?.getAttribute('data-code-raw') ?? '';
      if (raw) {
        void navigator.clipboard.writeText(raw).then(() => {
          const label = copyBtn.querySelector('.msg-codeblock-copy-label');
          if (label) {
            const prev = label.textContent;
            label.textContent = 'Copied';
            copyBtn.classList.add('msg-codeblock-copied');
            setTimeout(() => {
              label.textContent = prev;
              copyBtn.classList.remove('msg-codeblock-copied');
            }, 1400);
          }
        }).catch(() => showToast('Copy failed', 'err'));
      }
    }
  });

  // Double-click own message to edit (Discord behavior)
  messagesEl.addEventListener('dblclick', (e) => {
    const msgEl = (e.target as HTMLElement).closest('.msg');
    if (!msgEl) return;
    const authorId = msgEl.getAttribute('data-author');
    const myId = state.ownerId || state.people[0]?.id || '';
    if (authorId === myId) {
      const msgId = msgEl.getAttribute('data-id') ?? '';
      startInlineEdit(msgId, messagesEl);
    }
  });

  // Right-click context menu on messages
  messagesEl.addEventListener('contextmenu', (e) => {
    const msgEl = (e.target as HTMLElement).closest('.msg');
    if (!msgEl) return;
    e.preventDefault();

    const msgId = msgEl.getAttribute('data-id') ?? '';
    const authorId = msgEl.getAttribute('data-author') ?? '';
    const myId = state.ownerId || state.people[0]?.id || '';
    const isOwn = authorId === myId;
    const msg = messageCache.get(msgId);
    const content = msg?.content || '';

    const items: ContextMenuItem[] = [
      { label: 'Reply', icon: '&#8617;', action: () => { if (msg) openReplyBarFor(msg); }},
      { label: 'Create Thread', icon: '&#128488;', action: () => {
        if (msg) void openThreadPanel(msg);
      }},
      { label: 'Copy Text', icon: '&#128203;', action: () => {
        void navigator.clipboard.writeText(content);
        showToast('Copied', 'ok');
      }},
      { label: 'Pin Message', icon: '&#128204;', action: () => {
        void post('/api/messages/pin', { id: msgId, pinned: true }).then(() => showToast('Pinned', 'ok'));
      }},
      { label: 'Copy Message Link', icon: '&#128279;', action: () => {
        const link = `${window.location.origin}${window.location.pathname}?server=${state.currentServer}&channel=${state.currentChannel}&msg=${msgId}`;
        void navigator.clipboard.writeText(link);
        showToast('Link copied', 'ok');
      }},
      { label: 'Copy Message ID', icon: '#', action: () => {
        void navigator.clipboard.writeText(msgId);
        showToast('ID copied', 'ok');
      }},
    ];

    if (isOwn) {
      items.push({ label: '', divider: true });
      items.push({ label: 'Edit', icon: '&#9998;', action: () => {
        startInlineEdit(msgId, messagesEl);
      }});
      items.push({ label: 'Delete', icon: '&#128465;', danger: true, action: () => {
        confirmModal('Delete message', 'Delete this message?', () => {
          void post('/api/messages/delete', { id: msgId }).then(() => msgEl.remove());
        }, { confirmLabel: 'Delete', variant: 'danger' });
      }});
    }

    showContextMenu(e.clientX, e.clientY, items);
  });

  // ── Mobile long-press action sheet (hp_discord_mobile_v2) ──
  if (state.flags['hp_discord_mobile_v2'] !== false && window.innerWidth <= 768) {
    let lpTimer: ReturnType<typeof setTimeout> | null = null;
    let lpMsgEl: HTMLElement | null = null;

    messagesEl.addEventListener('touchstart', (e) => {
      const target = (e.target as HTMLElement).closest('.msg') as HTMLElement | null;
      if (!target) return;
      lpMsgEl = target;
      lpTimer = setTimeout(() => {
        if (!lpMsgEl) return;
        // Haptic feedback if available
        if (navigator.vibrate) navigator.vibrate(30);
        const msgId = lpMsgEl.getAttribute('data-id') ?? '';
        const authorId = lpMsgEl.getAttribute('data-author') ?? '';
        const myOwnId = state.ownerId || state.people[0]?.id || '';
        const isOwn = authorId === myOwnId;
        const msg = messageCache.get(msgId);
        if (!msg) return;
        showMobileActionSheet(msg, isOwn, messagesEl);
        lpMsgEl = null;
      }, 500);
    }, { passive: true });

    messagesEl.addEventListener('touchmove', () => {
      if (lpTimer) { clearTimeout(lpTimer); lpTimer = null; }
    }, { passive: true });

    messagesEl.addEventListener('touchend', () => {
      if (lpTimer) { clearTimeout(lpTimer); lpTimer = null; }
    }, { passive: true });
  }

  // ── Swipe-right-to-reply (universal mobile chat gesture) ──
  // Discord/Telegram/WhatsApp/Signal all do this: drag a message right ~60px,
  // it reveals a reply icon under it, and on release opens the reply bar for
  // that message. We track per-touch state so multi-finger gestures (pinch in
  // an embedded image) don't interfere.
  if (window.matchMedia('(pointer: coarse)').matches) {
    let swipeMsgEl: HTMLElement | null = null;
    let swipeStartX = 0;
    let swipeStartY = 0;
    let swipeActive = false; // becomes true once we commit (block scroll)
    const SWIPE_TRIGGER_PX = 56;
    const SWIPE_MAX_PX = 80; // was 96 — tightened so messages don't pan further than feels deliberate
    const SWIPE_HORIZONTAL_BIAS = 2.2; // was 1.4 — Mitchell flagged messages "panning" on slightly horizontal vertical scrolls; raise the bias so dx must clearly dominate dy before we commit

    const resetSwipe = (animate: boolean): void => {
      if (!swipeMsgEl) return;
      if (animate) {
        swipeMsgEl.style.transition = 'transform 180ms cubic-bezier(.2,.8,.2,1)';
        swipeMsgEl.style.transform = '';
        swipeMsgEl.classList.remove('msg-swiping', 'msg-swipe-armed');
        const el = swipeMsgEl;
        setTimeout(() => { el.style.transition = ''; }, 200);
      } else {
        swipeMsgEl.style.transition = '';
        swipeMsgEl.style.transform = '';
        swipeMsgEl.classList.remove('msg-swiping', 'msg-swipe-armed');
      }
      swipeMsgEl = null;
      swipeActive = false;
    };

    messagesEl.addEventListener('touchstart', (e: TouchEvent) => {
      if (e.touches.length !== 1) { resetSwipe(false); return; }
      const t = e.touches[0];
      if (!t) return;
      const target = (e.target as HTMLElement).closest('.msg') as HTMLElement | null;
      if (!target) return;
      // Skip on attachments / interactive children — clicking an image,
      // reaction chip, or link should never start a swipe-to-reply.
      const tag = (e.target as HTMLElement).tagName.toLowerCase();
      if (tag === 'img' || tag === 'a' || tag === 'button' || tag === 'video') return;
      swipeMsgEl = target;
      swipeStartX = t.clientX;
      swipeStartY = t.clientY;
      swipeActive = false;
    }, { passive: true });

    messagesEl.addEventListener('touchmove', (e: TouchEvent) => {
      if (!swipeMsgEl || e.touches.length !== 1) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - swipeStartX;
      const dy = Math.abs(t.clientY - swipeStartY);
      // Only commit to a swipe gesture if it's mostly horizontal and rightward.
      // dx threshold raised 12 → 18 so the swipe needs deliberate intent
      // before the message starts visibly panning (Mitchell 2026-04-25).
      if (!swipeActive) {
        if (dx > 18 && dx > dy * SWIPE_HORIZONTAL_BIAS) {
          swipeActive = true;
          swipeMsgEl.classList.add('msg-swiping');
        } else if (dy > 8) {
          // Vertical scroll won — give up the swipe entirely.
          resetSwipe(false);
          return;
        } else {
          return;
        }
      }
      const tx = Math.max(0, Math.min(SWIPE_MAX_PX, dx));
      swipeMsgEl.style.transform = `translateX(${tx}px)`;
      const armed = tx >= SWIPE_TRIGGER_PX;
      swipeMsgEl.classList.toggle('msg-swipe-armed', armed);
    }, { passive: true });

    messagesEl.addEventListener('touchend', () => {
      if (!swipeMsgEl) return;
      const armed = swipeMsgEl.classList.contains('msg-swipe-armed');
      if (armed) {
        const msgId = swipeMsgEl.getAttribute('data-id') ?? '';
        const msg = messageCache.get(msgId);
        if (msg) {
          if (navigator.vibrate) navigator.vibrate(15);
          openReplyBarFor(msg);
        }
      }
      resetSwipe(true);
    }, { passive: true });

    messagesEl.addEventListener('touchcancel', () => resetSwipe(true), { passive: true });
  }

  // ── Pull-to-load-older messages (works on touch + scroll wheel) ──
  // When the user reaches the top of the list and continues to scroll up
  // (or drags down on touch), trigger an older-messages fetch with a small
  // visible "Loading older messages…" pill at the top. Only fire once per
  // batch — debounce 600ms, and only if there are likely more messages.
  let loadOlderBusy = false;
  let loadOlderArmed = false;
  let pullStartY = 0;
  const olderPill = document.createElement('div');
  olderPill.className = 'chat-older-pill';
  olderPill.textContent = 'Loading older messages…';
  olderPill.style.display = 'none';
  messagesEl.parentElement?.insertBefore(olderPill, messagesEl);

  const triggerLoadOlder = async (): Promise<void> => {
    if (loadOlderBusy) return;
    loadOlderBusy = true;
    olderPill.style.display = 'block';
    try {
      const oldFirst = messagesEl.querySelector('[data-id]') as HTMLElement | null;
      const beforeId = oldFirst?.getAttribute('data-id');
      // The API `before` cursor is a created_at timestamp, NOT a message id —
      // pass the oldest loaded message's timestamp so older pages actually load.
      const beforeTs = beforeId ? messageCache.get(beforeId)?.created_at : undefined;
      if (!beforeTs) return;
      const prevHeight = messagesEl.scrollHeight;
      await loadOlderMessages(sid, channel, messagesEl, beforeTs);
      // Anchor scroll position so the user doesn't get teleported.
      messagesEl.scrollTop = messagesEl.scrollHeight - prevHeight;
    } catch { /* swallow — pill hides anyway */ }
    finally {
      olderPill.style.display = 'none';
      setTimeout(() => { loadOlderBusy = false; }, 600);
    }
  };

  messagesEl.addEventListener('touchstart', (e: TouchEvent) => {
    if (messagesEl.scrollTop > 8) { loadOlderArmed = false; return; }
    pullStartY = e.touches[0]?.clientY ?? 0;
    loadOlderArmed = true;
  }, { passive: true });
  messagesEl.addEventListener('touchmove', (e: TouchEvent) => {
    if (!loadOlderArmed) return;
    const dy = (e.touches[0]?.clientY ?? 0) - pullStartY;
    if (dy > 60 && messagesEl.scrollTop <= 8) {
      loadOlderArmed = false;
      void triggerLoadOlder();
    }
  }, { passive: true });

  // Desktop equivalent: when scroll reaches 0 and user keeps wheeling up.
  messagesEl.addEventListener('wheel', (e: WheelEvent) => {
    if (messagesEl.scrollTop <= 4 && e.deltaY < -20) void triggerLoadOlder();
  }, { passive: true });

  // Presence heartbeat every 30s
  const myId = state.ownerId || state.people[0]?.id || '';
  if (myId) {
    const hb = () => void post('/api/presence/heartbeat', {
      person_id: myId,
      server_id: sid,
      status: localStorage.getItem('hub-people-status') || 'online',
    }).catch(() => {});
    hb();
    const hbTimer = setInterval(hb, 30000);
    // Clean up on channel switch
    const w = window as unknown as Record<string, unknown>;
    const origCleanup = w['__hpCleanup'] as (() => void) | undefined;
    w['__hpCleanup'] = () => {
      clearInterval(hbTimer);
      origCleanup?.();
    };
  }

  // Realtime delivery mode \u2014 user choice persisted in localStorage.
  // 'realtime'   = SSE push only (default, lowest latency, depends on
  //                EventSource staying open through whatever proxy chain).
  // 'auto-2s'    = poll every 2s (fallback when SSE fails / encrypted
  //                channels add too much latency / Mitchell prefers it).
  // 'auto-10s'   = slower poll, lighter on the bus.
  // 'manual'     = no auto-refresh; user pulls down to load.
  // Read from localStorage; respect kill-switch flag as a master off.
  type RtMode = 'realtime' | 'auto-2s' | 'auto-10s' | 'manual';
  const rtMode: RtMode = state.flags['ws_realtime'] === false
    ? 'auto-10s'
    : ((localStorage.getItem('hub-people-rt-mode') as RtMode | null) || 'realtime');

  // Render the connection-mode indicator in the chat header (small dot
  // + label + tooltip). Lets the user see WHY messages aren't showing.
  installRealtimeStatusIndicator(rtMode);

  if (rtMode === 'realtime') {
    wireRealtime(sid, channel, messagesEl, typingEl);
  } else if (rtMode !== 'manual') {
    const intervalMs = rtMode === 'auto-2s' ? 2000 : 10000;
    pollTimer = setInterval(() => {
      pollNewMessages(sid, channel, messagesEl);
      pollTyping(sid, channel, typingEl);
    }, intervalMs);
  }
  // Listen for user changing the mode in Settings \u2014 re-init without
  // requiring a page reload.
  window.addEventListener('hub-people-rt-mode-changed', () => {
    window.location.hash = window.location.hash; // no-op refresh trigger
    void renderChat(body, channel);
  }, { once: true });
}

/** Small connection-status pill that lives in the chat header so the user
 *  always knows whether messages are pushed live, polled, or off. Click
 *  the pill to open the realtime-mode picker. */
function installRealtimeStatusIndicator(mode: 'realtime' | 'auto-2s' | 'auto-10s' | 'manual'): void {
  const host = document.getElementById('ca-rt-status') as HTMLElement | null;
  if (!host) return;
  const labelMap = {
    'realtime': { label: 'Live', cls: 'rt-dot-live', tip: 'Real-time push (SSE).' },
    'auto-2s':  { label: 'Auto 2s', cls: 'rt-dot-poll', tip: 'Auto-refresh every 2 seconds.' },
    'auto-10s': { label: 'Auto 10s', cls: 'rt-dot-slow', tip: 'Auto-refresh every 10 seconds.' },
    'manual':   { label: 'Manual', cls: 'rt-dot-off', tip: 'Manual refresh only \u2014 pull down to load.' },
  };
  const m = labelMap[mode];
  host.innerHTML = `<span class="rt-dot ${m.cls}" aria-hidden="true"></span><span class="rt-label">${m.label}</span>`;
  host.title = m.tip + ' Click to change.';
  host.style.cursor = 'pointer';
  host.onclick = () => {
    void openRealtimeModePicker(host);
  };
  host.onkeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      void openRealtimeModePicker(host);
    }
  };
  // Update the dot live as the SSE connection state flips so the user
  // can see "reconnecting" vs "open" without opening DevTools.
  if (mode === 'realtime') {
    realtime.onConnectionChange((cs) => {
      const dot = host.querySelector('.rt-dot') as HTMLElement | null;
      if (!dot) return;
      dot.classList.remove('rt-dot-live', 'rt-dot-reconn', 'rt-dot-off');
      if (cs === 'open') { dot.classList.add('rt-dot-live'); host.querySelector('.rt-label')!.textContent = 'Live'; }
      else if (cs === 'reconnecting' || cs === 'connecting') { dot.classList.add('rt-dot-reconn'); host.querySelector('.rt-label')!.textContent = 'Reconnecting\u2026'; }
      else { dot.classList.add('rt-dot-off'); host.querySelector('.rt-label')!.textContent = 'Disconnected'; }
    });
  }
}

/** Mode picker popover \u2014 click the rt-status pill to swap delivery mode
 *  on the fly. Persists to localStorage and dispatches a custom event so
 *  the chat re-mounts with the new mode without a page reload. */
async function openRealtimeModePicker(anchor: HTMLElement): Promise<void> {
  document.querySelectorAll('.rt-mode-pop').forEach(el => el.remove());
  const cur = (localStorage.getItem('hub-people-rt-mode') as string) || 'realtime';
  const opts = [
    { key: 'realtime',  label: 'Real-time (push)', desc: 'Lowest latency. Uses SSE.' },
    { key: 'auto-2s',   label: 'Auto-refresh 2s',  desc: 'Polls every 2 seconds. Use if push feels sluggish.' },
    { key: 'auto-10s',  label: 'Auto-refresh 10s', desc: 'Polls every 10 seconds. Lighter on the network.' },
    { key: 'manual',    label: 'Manual',           desc: 'No auto-refresh. Pull down at the top to load newer.' },
  ];
  const pop = document.createElement('div');
  pop.className = 'rt-mode-pop cl-dev-pop';
  pop.setAttribute('role', 'menu');
  pop.innerHTML = `
    <div class="cl-dev-pop-title">Message delivery</div>
    ${opts.map(o => {
      const checked = o.key === cur;
      return `<button type="button" class="cl-dev-pop-item rt-mode-item${checked ? ' selected' : ''}" data-mode="${esc(o.key)}" role="menuitemradio" aria-checked="${checked}">
        <span class="cl-dev-pop-check">${checked ? '\u2713' : ''}</span>
        <span class="cl-dev-pop-label"><strong>${esc(o.label)}</strong><br><span class="rt-mode-desc">${esc(o.desc)}</span></span>
      </button>`;
    }).join('')}
  `;
  document.body.appendChild(pop);
  const r = anchor.getBoundingClientRect();
  const margin = 8;
  const popWidth = 280;
  pop.style.position = 'fixed';
  pop.style.top = `${r.bottom + 6}px`;
  // Right-align with the pill when possible, then clamp to the viewport so
  // the popover never overflows the right edge on tablet widths (541-768px)
  // where the mobile bottom-sheet override does not apply.
  const maxLeft = window.innerWidth - popWidth - margin;
  const idealLeft = r.right - popWidth;
  pop.style.left = `${Math.max(margin, Math.min(idealLeft, maxLeft))}px`;
  pop.style.minWidth = `${popWidth}px`;

  const items = Array.from(pop.querySelectorAll<HTMLButtonElement>('.rt-mode-item'));
  const focusIdx = (i: number): void => {
    const n = items.length;
    const idx = ((i % n) + n) % n;
    items[idx]?.focus();
  };
  const selectedIdx = items.findIndex(b => b.classList.contains('selected'));
  setTimeout(() => focusIdx(selectedIdx >= 0 ? selectedIdx : 0), 0);

  const close = (): void => {
    pop.remove();
    document.removeEventListener('click', off);
    document.removeEventListener('keydown', onKey);
    anchor.focus();
  };
  const off = (e: MouseEvent): void => { if (!pop.contains(e.target as Node) && e.target !== anchor) close(); };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    const cur = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); focusIdx(cur < 0 ? 0 : cur + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focusIdx(cur < 0 ? items.length - 1 : cur - 1); }
    else if (e.key === 'Home') { e.preventDefault(); focusIdx(0); }
    else if (e.key === 'End') { e.preventDefault(); focusIdx(items.length - 1); }
  };
  setTimeout(() => document.addEventListener('click', off), 0);
  document.addEventListener('keydown', onKey);

  items.forEach(btn => {
    btn.addEventListener('click', () => {
      const mode = btn.dataset['mode'] ?? 'realtime';
      localStorage.setItem('hub-people-rt-mode', mode);
      window.dispatchEvent(new CustomEvent('hub-people-rt-mode-changed', { detail: { mode } }));
      // Hard reload current channel via the hash router so the new mode
      // takes effect immediately (cheaper than reloading whole page).
      const h = window.location.hash;
      window.location.hash = '#';
      setTimeout(() => { window.location.hash = h; }, 10);
      close();
    });
  });
}

function wireRealtime(serverId: string, channel: string, messagesEl: HTMLElement, typingEl: HTMLElement): void {
  // Tear down any prior subscription before swapping channels.
  for (const off of realtimeUnsubs) off();
  realtimeUnsubs = [];
  if (activeChannelTopic) realtime.unsubscribeChannel(activeChannelTopic);

  const topic = channelTopic(serverId, channel);
  activeChannelTopic = topic;
  realtime.connect();
  realtime.subscribeChannel(topic);

  const myId = state.ownerId || state.people[0]?.id || '';
  const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();
  const currentTypers = new Set<string>();

  const renderTyping = (): void => {
    const others = Array.from(currentTypers).filter(id => id !== myId);
    if (others.length === 0) { typingEl.textContent = ''; return; }
    const names = others.map(id => authorName(id));
    const dots = '<span class="typing-dots"><span></span><span></span><span></span></span>';
    if (names.length === 1) {
      typingEl.innerHTML = `${dots} <strong>${esc(names[0] || '')}</strong> is typing...`;
    } else if (names.length <= 3) {
      typingEl.innerHTML = `${dots} <strong>${names.map(esc).join(', ')}</strong> are typing...`;
    } else {
      typingEl.innerHTML = `${dots} Several people are typing...`;
    }
  };

  const off = realtime.on((ev: RealtimeEvent) => {
    // Filter to the channel we're currently rendering — events for other
    // subscriptions (e.g. a thread panel) are routed by their own listeners.
    if (ev.type === 'message_created' && ev.channel_id === topic && !ev.thread_id) {
      const msg = ev.message;
      if (messagesEl.querySelector(`[data-id="${msg.id}"]`)) return;
      messageCache.set(msg.id, msg as Message);
      const msgs = messagesEl.querySelectorAll('.msg');
      const lastEl = msgs[msgs.length - 1];
      const lastAuthor = lastEl?.getAttribute('data-author');
      const showHeader = lastAuthor !== msg.author_id;
      messagesEl.insertAdjacentHTML('beforeend', renderMessage(msg as Message, showHeader));
      lastMessageId = msg.id;
      // Discord-parity: brief slide-up + fade-in on the freshly-arrived row.
      const newEl = messagesEl.querySelector(`[data-id="${msg.id}"]`);
      if (newEl) {
        newEl.classList.add('msg-arrival');
        setTimeout(() => newEl.classList.remove('msg-arrival'), 360);
      }
      const nearBottom = messagesEl.scrollHeight - messagesEl.scrollTop - messagesEl.clientHeight < 100;
      if (nearBottom) {
        // Smooth-scroll only when we're already pinned — jumping mid-read
        // would yank the user away from older messages they're reading.
        messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: 'smooth' });
      } else {
        // Bump the "X new messages" pill so the user knows there's traffic.
        const bump = (window as unknown as Record<string, unknown>)['__hpBumpUnseen'] as (() => void) | undefined;
        bump?.();
      }
      if (msg.author_id !== myId) {
        // Per brief: plain channel messages don't notify/ping. Only @mention
        // of the current user, or a reply to one of their messages, fires
        // a sound + popup. Server mode + channel mute gate inside notify().
        const myName = (state.people.find(p => p.id === myId)?.name ?? '').trim();
        const mentionTag = myName ? `@${myName.replace(/\s+/g, '')}` : '';
        const isMention = !!mentionTag && msg.content.toLowerCase().includes(mentionTag.toLowerCase());
        const parent = msg.reply_to ? messageCache.get(msg.reply_to) : null;
        const isReplyToMe = !!(parent && parent.author_id === myId);
        if (isMention || isReplyToMe) {
          const senderPerson = state.people.find(p => p.id === msg.author_id);
          const name = senderPerson?.name || authorName(msg.author_id);
          const preview = msg.content.length > 80 ? msg.content.substring(0, 80) + '...' : msg.content;
          notify({
            kind: isMention ? 'mention' : 'reply',
            title: isMention
              ? `${name} mentioned you in #${channel}`
              : `${name} replied to you in #${channel}`,
            body: preview || 'Sent an attachment',
            channel,
            serverId,
            icon: senderPerson?.avatar || undefined,
          });
        }
      }
    } else if (ev.type === 'message_edited' && ev.channel_id === topic && !ev.thread_id) {
      const msg = ev.message;
      messageCache.set(msg.id, msg as Message);
      const oldEl = messagesEl.querySelector(`[data-id="${msg.id}"]`);
      if (oldEl) {
        const wrap = document.createElement('div');
        wrap.innerHTML = renderMessage(msg as Message, !oldEl.classList.contains('msg-compact'));
        const fresh = wrap.firstElementChild;
        if (fresh) oldEl.replaceWith(fresh);
      }
    } else if (ev.type === 'message_deleted' && ev.channel_id === topic && !ev.thread_id) {
      messageCache.delete(ev.message_id);
      messagesEl.querySelector(`[data-id="${ev.message_id}"]`)?.remove();
    } else if (ev.type === 'reaction_changed' && ev.channel_id === topic) {
      // Re-render the affected message's reactions row from cache + the
      // pushed reactions array. Cheap approach: refetch via REST in the
      // background and let the existing reaction renderer handle it.
      void refreshReactions(ev.message_id);
    } else if (ev.type === 'typing_changed' && ev.channel_id === topic) {
      if (ev.identity_id === myId) return;
      currentTypers.add(ev.identity_id);
      const prev = typingTimers.get(ev.identity_id);
      if (prev) clearTimeout(prev);
      typingTimers.set(ev.identity_id, setTimeout(() => {
        currentTypers.delete(ev.identity_id);
        typingTimers.delete(ev.identity_id);
        renderTyping();
      }, 6500));
      renderTyping();
    } else if (ev.type === 'resync') {
      // Bus lagged — reload the channel from REST to recover lost frames.
      void loadMessages(serverId, channel, messagesEl);
    }
  });
  // Gap-heal on reconnect: whenever the SSE state flips back to 'open' after
  // a reconnect/sleep cycle, refetch everything newer than the last message
  // we rendered so events dropped during the offline window don't vanish.
  let wasReconnecting = false;
  const offState = realtime.onConnectionChange((cs) => {
    if (cs === 'reconnecting' || cs === 'closed') {
      wasReconnecting = true;
    } else if (cs === 'open' && wasReconnecting) {
      wasReconnecting = false;
      void pollNewMessages(serverId, channel, messagesEl);
    }
  });

  // Stale-tab heal: a backgrounded tab, a slept laptop, or a proxy idle-kill
  // can leave the SSE in a zombie state (no `error` fires, so the reconnect
  // gap-heal above never triggers) — the channel then stops showing new posts
  // until a manual refresh / server switch. When the tab regains visibility or
  // the network returns we (1) poll for anything newer than the last rendered
  // message so missed posts appear immediately with NO refresh, and (2) kick
  // the realtime client to rebuild its stream if it's no longer open so live
  // delivery resumes. Both are idempotent + cheap; the poll-only-when-visible
  // guard avoids churn while hidden.
  const healActive = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (realtime.getState() !== 'open') realtime.connect();
    void pollNewMessages(serverId, channel, messagesEl);
  };
  document.addEventListener('visibilitychange', healActive);
  window.addEventListener('online', healActive);

  realtimeUnsubs.push(off, offState, () => {
    for (const t of typingTimers.values()) clearTimeout(t);
  }, () => {
    document.removeEventListener('visibilitychange', healActive);
    window.removeEventListener('online', healActive);
  });
}

async function refreshReactions(messageId: string): Promise<void> {
  try {
    const myId = state.ownerId || state.people[0]?.id || '';
    const groups = await get<Array<{ emoji: string; count: number; me: boolean }>>(
      `/api/reactions?message_id=${encodeURIComponent(messageId)}&my_id=${encodeURIComponent(myId)}`,
    );
    const cached = messageCache.get(messageId);
    if (!cached) return;
    cached.reactions = JSON.stringify(groups);
    const oldEl = document.querySelector(`[data-id="${messageId}"]`);
    if (oldEl) {
      const wrap = document.createElement('div');
      wrap.innerHTML = renderMessage(cached, oldEl.classList.contains('msg-full'));
      const fresh = wrap.firstElementChild;
      if (fresh) oldEl.replaceWith(fresh);
    }
  } catch { /* silent */ }
}

/** Mobile bottom-sheet action menu for messages (Discord-style long-press). */
function showMobileActionSheet(msg: Message, isOwn: boolean, messagesEl: HTMLElement): void {
  // Remove any existing sheet
  document.querySelector('.mob-action-sheet-backdrop')?.remove();
  document.querySelector('.mob-action-sheet')?.remove();

  const name = authorName(msg.author_id);
  const preview = (msg.content || '').substring(0, 80) || '(attachment)';

  const backdrop = document.createElement('div');
  backdrop.className = 'mob-action-sheet-backdrop';

  const sheet = document.createElement('div');
  sheet.className = 'mob-action-sheet';

  const svgReact = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>';
  const svgReply = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>';
  const svgCopy = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
  const svgPin = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24z"/></svg>';
  const svgEdit = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
  const svgDelete = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';

  let items = `
    <div class="mob-sheet-handle"></div>
    <div class="mob-sheet-preview">
      <div class="mob-sheet-preview-author">${esc(name)}</div>
      <div class="mob-sheet-preview-text">${esc(preview)}</div>
    </div>
    <button class="mob-sheet-item" data-act="react">${svgReact} Add Reaction</button>
    <button class="mob-sheet-item" data-act="reply">${svgReply} Reply</button>
    <button class="mob-sheet-item" data-act="copy">${svgCopy} Copy Text</button>
    <button class="mob-sheet-item" data-act="pin">${svgPin} ${msg.pinned ? 'Unpin' : 'Pin'} Message</button>`;

  if (isOwn) {
    items += `<div class="mob-sheet-divider"></div>
      <button class="mob-sheet-item" data-act="edit">${svgEdit} Edit Message</button>
      <button class="mob-sheet-item danger" data-act="delete">${svgDelete} Delete Message</button>`;
  }

  sheet.innerHTML = items;
  document.body.appendChild(backdrop);
  document.body.appendChild(sheet);

  let closing = false;
  const close = (): void => {
    if (closing) return;
    closing = true;
    backdrop.classList.add('mob-sheet-closing');
    sheet.classList.add('mob-sheet-closing');
    const cleanup = (): void => { backdrop.remove(); sheet.remove(); };
    sheet.addEventListener('animationend', cleanup, { once: true });
    window.setTimeout(cleanup, 240);
  };
  backdrop.addEventListener('click', close);

  sheet.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
    if (!btn) return;
    const act = btn.dataset['act'];
    close();
    if (act === 'react') {
      // Show emoji picker near compose area
      const composeBtn = document.querySelector<HTMLButtonElement>('.chat-send-btn');
      if (composeBtn) showEmojiPicker(composeBtn, msg.id, messagesEl);
    } else if (act === 'reply') {
      openReplyBarFor(msg);
    } else if (act === 'copy') {
      void navigator.clipboard.writeText(msg.content || '');
      showToast('Copied', 'ok');
    } else if (act === 'pin') {
      void post('/api/messages/pin', { id: msg.id, pinned: !msg.pinned }).then(() =>
        showToast(msg.pinned ? 'Unpinned' : 'Pinned', 'ok'));
    } else if (act === 'edit') {
      startInlineEdit(msg.id, messagesEl);
    } else if (act === 'delete') {
      confirmModal('Delete message', 'Delete this message?', () => {
        void post('/api/messages/delete', { id: msg.id }).then(() => {
          messagesEl.querySelector(`[data-id="${msg.id}"]`)?.remove();
        });
      }, { confirmLabel: 'Delete', variant: 'danger' });
    }
  });
}

/** Prepend N older messages above the current first message in the channel
 *  (cursor: before=<msgId>). Used by the pull-to-load-older gesture and the
 *  desktop wheel-up-at-top equivalent. Idempotent — if the API returns no
 *  older messages we leave the DOM unchanged so the caller can stop pulling. */
async function loadOlderMessages(
  serverId: string,
  channel: string,
  el: HTMLElement,
  beforeTs: string,
): Promise<number> {
  try {
    const pid = state.ownerId ?? '';
    const msgs = await get<Message[]>(
      `/api/messages?server_id=${encodeURIComponent(serverId)}&channel=${encodeURIComponent(channel)}&before=${encodeURIComponent(beforeTs)}&limit=50&person_id=${encodeURIComponent(pid)}`,
    );
    if (!msgs || msgs.length === 0) return 0;
    const sorted = msgs.slice().reverse(); // API returns newest first
    // Deduplicate: skip any message already in the DOM (rendered or virtualized placeholder)
    const fresh = sorted.filter(m => !el.querySelector(`[data-id="${m.id}"]`));
    if (fresh.length === 0) return 0;
    for (const m of fresh) messageCache.set(m.id, m);
    let html = '';
    for (let i = 0; i < fresh.length; i++) {
      const msg = fresh[i] as Message;
      const prev = i > 0 ? (fresh[i - 1] as Message) : null;
      html += renderMessage(msg, shouldShowHeader(msg, prev));
    }
    el.insertAdjacentHTML('afterbegin', html);
    return fresh.length;
  } catch {
    return 0;
  }
}

async function loadMessages(serverId: string, channel: string, el: HTMLElement): Promise<void> {
  try {
    const pid = state.ownerId ?? '';
    const msgs = await get<Message[]>(`/api/messages?server_id=${encodeURIComponent(serverId)}&channel=${encodeURIComponent(channel)}&limit=50&person_id=${encodeURIComponent(pid)}`);
    // Reverse since API returns newest first
    const sorted = msgs.reverse();

    if (sorted.length === 0) {
      el.innerHTML = `<div class="chat-empty">
        <div class="chat-empty-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="56" height="56" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="4" y1="9" x2="20" y2="9"/>
            <line x1="4" y1="15" x2="20" y2="15"/>
            <line x1="10" y1="3" x2="8" y2="21"/>
            <line x1="16" y1="3" x2="14" y2="21"/>
          </svg>
        </div>
        <div class="chat-empty-title">Welcome to #${esc(channel)}!</div>
        <div class="chat-empty-sub">This is the start of the <strong>#${esc(channel)}</strong> channel. Send a message to break the ice.</div>
      </div>`;
      return;
    }

    // Populate message cache for reply lookups
    messageCache.clear();
    for (const m of sorted) messageCache.set(m.id, m);

    // Discord-style unread marker. Last-read timestamp is persisted per
    // channel; the divider drops above the first message whose created_at
    // exceeds it (and that isn't the user's own message — own messages
    // shouldn't trigger an unread marker against themselves).
    const lastReadKey = `hub-people-lastread:${serverId}:${channel}`;
    const lastReadIso = localStorage.getItem(lastReadKey) || '';
    const myId = state.ownerId || '';

    let html = '';
    let lastDate = '';
    let unreadInserted = false;
    for (let i = 0; i < sorted.length; i++) {
      const msg = sorted[i] as Message;
      const prev = i > 0 ? (sorted[i - 1] as Message) : null;

      // Date divider between days
      const msgDate = fmtDayHeading(msg.created_at);
      if (msgDate !== lastDate) {
        html += `<div class="msg-date-divider"><span>${msgDate}</span></div>`;
        lastDate = msgDate;
      }

      if (!unreadInserted && lastReadIso && msg.created_at > lastReadIso
          && msg.author_id !== myId && i > 0) {
        html += `<div class="unread-divider" id="unread-divider" role="separator" aria-label="New messages"><span>— New messages —</span></div>`;
        unreadInserted = true;
      }

      html += renderMessage(msg, shouldShowHeader(msg, prev));
    }
    el.innerHTML = html;
    const lastMsg = sorted[sorted.length - 1] as Message;
    lastMessageId = lastMsg.id;

    // Restore previous scroll position if we've been here before; otherwise go to bottom
    const remembered = channelScrollPositions.get(currentChannelKey);
    if (remembered !== undefined && remembered < el.scrollHeight - el.clientHeight - 10) {
      el.scrollTop = remembered;
    } else {
      el.scrollTop = el.scrollHeight;
    }

    // Virtualization: unmount messages scrolled far out of view for perf on long channels
    requestAnimationFrame(() => setupVirtualization(el));

    // Fetch URL embeds for messages with links
    void loadEmbeds(el);

    // Batch-fetch thread summaries and paint "N replies" badges. For any
    // thread that is also a tracked dev session, fetch status_color in a
    // second batch and decorate the badge with a coloured dot.
    void fetchThreadSummaries(sorted.map(m => m.id)).then(async summaries => {
      const threadIds: string[] = [];
      summaries.forEach((summary) => {
        const msgEl = el.querySelector(`[data-id="${summary.parent_message_id}"]`);
        if (msgEl) renderThreadBadge(msgEl, summary);
        threadIds.push(summary.thread_id);
      });
      if (threadIds.length === 0) return;
      const statuses = await fetchThreadStatusBatch(threadIds);
      statuses.forEach((view) => {
        // Find the badge by thread_id and decorate it.
        const badge = el.querySelector<HTMLElement>(`.msg-thread-badge[data-thread="${view.thread_id}"]`);
        if (!badge || badge.querySelector('.ds-dot')) return;
        const dot = document.createElement('span');
        dot.className = `ds-dot ds-dot-${view.status_color}`;
        dot.title = `Dev session: ${view.status}`;
        badge.prepend(dot);
      });
    });

    // Clicking a thread badge opens the thread panel
    el.addEventListener('click', (e) => {
      const badge = (e.target as HTMLElement).closest('.msg-thread-badge');
      if (!badge) return;
      const msgEl = badge.closest('.msg');
      const parentId = msgEl?.getAttribute('data-id');
      if (!parentId) return;
      const parent = messageCache.get(parentId);
      if (parent) void openThreadPanel(parent);
    });

    // Wire up message quick-bar buttons (Discord-style hover toolbar).
    // Quick-bar lives on the .msg row, but event delegation off `el` lets
    // clicks land even when the bar is rendered into a re-keyed group.
    el.addEventListener('click', (e) => {
      const target = e.target as HTMLElement | null;
      const btn = target?.closest('.msg-quick-btn') as HTMLButtonElement | null;
      if (!btn) return;
      e.stopPropagation();
      const action = btn.dataset['action'];
      const msgId = btn.dataset['msg'] ?? '';
      // Resolve message from cache first, fall back to a DOM-derived stub
      // so a cold quick-bar click still works (Reply/Thread previously
      // silently no-op'd when the cache hadn't been populated yet).
      const resolveMsg = (): Message | null => {
        const cached = messageCache.get(msgId);
        if (cached) return cached;
        const row = el.querySelector(`.msg[data-id="${msgId}"]`) as HTMLElement | null;
        if (!row) return null;
        return {
          id: msgId,
          server_id: state.currentServer || '',
          channel: String(state.currentChannel || ''),
          author_id: row.getAttribute('data-author') || '',
          content: row.querySelector('.msg-content')?.textContent || '',
          edited_at: null,
          reply_to: null,
          attachments: '[]',
          reactions: '[]',
          pinned: 0,
          created_at: new Date().toISOString(),
        };
      };

      if (action === 'react') {
        showEmojiPicker(btn, msgId, el);
      } else if (action === 'pin') {
        const isPinned = btn.dataset['pinned'] === '1';
        void post('/api/messages/pin', { id: msgId, pinned: !isPinned }).then(() => {
          showToast(isPinned ? 'Message unpinned' : 'Message pinned', 'ok');
        }).catch(err => errToast(err));
      } else if (action === 'reply') {
        const targetMsg = resolveMsg();
        if (targetMsg) openReplyBarFor(targetMsg);
      } else if (action === 'thread') {
        const targetMsg = resolveMsg();
        if (targetMsg) void openThreadPanel(targetMsg);
      } else if (action === 'forward') {
          // Placeholder per spec (batch 7.5) — copy message link for now
          const link = `${window.location.origin}${window.location.pathname}?server=${state.currentServer}&channel=${state.currentChannel}&msg=${msgId}`;
          void navigator.clipboard.writeText(link);
          showToast('Forward is coming soon — link copied', 'ok');
        } else if (action === 'edit') {
          startInlineEdit(msgId, el);
        } else if (action === 'more') {
          // Reuse the right-click menu for the "More" dots
          const msgEl = el.querySelector(`[data-id="${msgId}"]`) as HTMLElement | null;
          if (msgEl) {
            const rect = btn.getBoundingClientRect();
            msgEl.dispatchEvent(new MouseEvent('contextmenu', {
              bubbles: true, cancelable: true,
              clientX: rect.left, clientY: rect.bottom + 4,
            }));
          }
        }
    });
  } catch {
    el.innerHTML = '<div class="chat-loading">Failed to load messages</div>';
  }
}

/** Render pending attachment previews */
function renderAttachPreview(el: HTMLElement): void {
  if (pendingFiles.length === 0) {
    el.style.display = 'none';
    el.innerHTML = '';
    return;
  }
  el.style.display = 'flex';
  el.innerHTML = pendingFiles.map((f, i) => {
    const isImage = f.type.startsWith('image/');
    const sizeStr = f.size < 1024 ? `${f.size}B` : f.size < 1048576 ? `${(f.size/1024).toFixed(1)}KB` : `${(f.size/1048576).toFixed(1)}MB`;
    const preview = isImage ? `<img src="${URL.createObjectURL(f)}" class="attach-thumb">` : `<div class="attach-file-icon"><svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" style="opacity:0.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg></div>`;
    return `<div class="attach-preview-item">
      ${preview}
      <div class="attach-preview-name">${esc(f.name)}</div>
      <div class="attach-preview-size">${sizeStr}</div>
      <button class="attach-remove" data-idx="${i}" title="Remove">&times;</button>
    </div>`;
  }).join('');

  el.querySelectorAll<HTMLButtonElement>('.attach-remove').forEach(btn => {
    btn.addEventListener('click', () => {
      const idx = parseInt(btn.dataset['idx'] ?? '0');
      pendingFiles.splice(idx, 1);
      renderAttachPreview(el);
    });
  });
}

/** Upload pending files and return attachment metadata */
async function uploadFiles(files: File[]): Promise<Attachment[]> {
  if (files.length === 0) return [];
  const formData = new FormData();
  for (const f of files) formData.append('files', f);

  const pid = encodeURIComponent(state.ownerId || '');
  const sid = encodeURIComponent(state.currentServer || '');
  const res = await fetch(`${API_BASE}/api/uploads?person_id=${pid}&server_id=${sid}`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });
  const data = await res.json();
  if (data.status === 'ok') {
    const payload = data.data;
    // Backend returns { files, storage_used_bytes, storage_limit_bytes }
    return (payload.files ?? payload) as Attachment[];
  }
  throw new Error(data.error || 'Upload failed');
}

async function sendMessage(
  serverId: string,
  channel: string,
  messagesEl: HTMLElement,
  sendOpts: { reply_to?: string; mentionReplyAuthor?: boolean },
): Promise<void> {
  if (!composeHandle) return;
  const input = composeHandle.textarea;
  const content = input.value.trim();
  const hasFiles = pendingFiles.length > 0;
  if (!content && !hasFiles) return;

  const authorId = state.ownerId || state.people[0]?.id || '';
  // Capture the reply target _before_ clearing — the compose handle will
  // clear its own state on success.
  const replyTo = sendOpts.reply_to;
  const mentionReplyAuthor = sendOpts.mentionReplyAuthor ?? true;
  const replyMsg = replyTo ? messageCache.get(replyTo) : undefined;
  input.value = '';

  try {
    // Upload files first if any
    let attachments: Attachment[] = [];
    if (hasFiles) {
      attachments = await uploadFiles([...pendingFiles]);
      pendingFiles = [];
      const previewEl = composeHandle.attachPreview;
      if (previewEl) { previewEl.style.display = 'none'; previewEl.innerHTML = ''; }
    }

    // When the reply-author mention toggle is ON, prepend @author so
    // renderMarkdown highlights it + pings the author.
    let outgoing = content;
    if (replyMsg && mentionReplyAuthor) {
      const name = authorName(replyMsg.author_id).replace(/\s+/g, '');
      const mentionTag = `@${name}`;
      if (!outgoing.toLowerCase().startsWith(mentionTag.toLowerCase())) {
        outgoing = outgoing ? `${mentionTag} ${outgoing}` : mentionTag;
      }
    }

    const msg = await post<Message>('/api/messages/send', {
      server_id: serverId,
      channel,
      author_id: authorId,
      content: outgoing,
      reply_to: replyTo,
      attachments: attachments.length > 0 ? JSON.stringify(attachments) : undefined,
    });

    // Clear reply state + draft
    composeHandle.clearReplyTo();
    composeHandle.clearDraft();

    // Cache the new message
    messageCache.set(msg.id, msg);

    // Append to chat — but only if the SSE listener hasn't already rendered
    // it. Same data-id dedupe guard the realtime listener uses (line ~1846).
    // Without this guard, sending a message can show two copies: one from
    // POST resolving here, one from SSE message_created firing — whichever
    // wins the race adds the second copy. Mitchell hit this on cold-start
    // where SSE arrives before the POST response.
    if (!messagesEl.querySelector(`[data-id="${msg.id}"]`)) {
      const msgs = messagesEl.querySelectorAll('.msg');
      const lastMsg = msgs[msgs.length - 1];
      const lastAuthor = lastMsg?.getAttribute('data-author');
      const showHeader = lastAuthor !== msg.author_id;

      messagesEl.insertAdjacentHTML('beforeend', renderMessage(msg, showHeader));
    }
    lastMessageId = msg.id;
    // Reset height + scroll in one rAF to avoid the mobile keyboard
    // dismiss→reopen bounce (separate DOM ops cause focus loss).
    // The input is already focused — don't call focus() again.
    input.style.height = 'auto';
    input.dispatchEvent(new Event('input', { bubbles: true }));
    requestAnimationFrame(() => {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    });
  } catch (e) {
    showToast(String(e), 'err');
    input.value = content; // restore
  }
}

async function pollNewMessages(serverId: string, channel: string, el: HTMLElement): Promise<void> {
  if (!lastMessageId) return;
  try {
    const pid = state.ownerId ?? '';
    const msgs = await get<Message[]>(
      `/api/messages?server_id=${encodeURIComponent(serverId)}&channel=${encodeURIComponent(channel)}&after=${encodeURIComponent(lastMessageId)}&limit=20&person_id=${encodeURIComponent(pid)}`,
    );
    if (msgs.length === 0) return;

    const myId = state.ownerId || state.people[0]?.id || '';
    let newFromOthers = false;

    for (const msg of msgs) {
      // Check if already rendered
      if (el.querySelector(`[data-id="${msg.id}"]`)) continue;
      messageCache.set(msg.id, msg);
      const showHeader = true; // simplified for poll
      el.insertAdjacentHTML('beforeend', renderMessage(msg, showHeader));
      lastMessageId = msg.id;
      if (msg.author_id !== myId) newFromOthers = true;
    }

    // Notifications: only fire for @mentions (brief rule — plain channel
     // messages don't spam). Mentions still fire even when channel is muted
     // (Discord parity). Sound is inside notify().
    if (newFromOthers) {
      const myName = (state.people.find(p => p.id === myId)?.name ?? '').trim();
      const lastNewMsg = msgs[msgs.length - 1];
      if (myName && lastNewMsg && lastNewMsg.author_id !== myId) {
        const mentionTag = `@${myName.replace(/\s+/g, '')}`;
        const isMention = lastNewMsg.content.toLowerCase().includes(mentionTag.toLowerCase());
        if (isMention && !isChannelMuted(`mention-suppress:${channel}`)) {
          const senderPerson = state.people.find(p => p.id === lastNewMsg.author_id);
          const name = senderPerson?.name || authorName(lastNewMsg.author_id);
          const preview = lastNewMsg.content.length > 80 ? lastNewMsg.content.substring(0, 80) + '...' : lastNewMsg.content;
          notify({
            kind: 'mention',
            title: `${name} mentioned you in #${channel}`,
            body: preview || 'Sent an attachment',
            channel,
            serverId,
            icon: senderPerson?.avatar || undefined,
          });
        }
      }
    }

    // Auto-scroll if near bottom, or show "new messages" bar
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    if (nearBottom) {
      el.scrollTop = el.scrollHeight;
    } else if (newFromOthers) {
      // Show new messages bar at top of chat
      let newBar = el.querySelector('.chat-new-msgs-bar') as HTMLElement;
      if (!newBar) {
        newBar = document.createElement('div');
        newBar.className = 'chat-new-msgs-bar';
        newBar.addEventListener('click', () => {
          el.scrollTop = el.scrollHeight;
          newBar.remove();
        });
        el.prepend(newBar);
      }
      const count = parseInt(newBar.dataset['count'] || '0') + msgs.filter(m => m.author_id !== myId).length;
      newBar.dataset['count'] = String(count);
      newBar.textContent = `${count} new message${count !== 1 ? 's' : ''} — Click to jump`;
    }
    // Connection restored
    if (pollFailCount > 0) {
      pollFailCount = 0;
      const bar = document.querySelector('.chat-connection-bar');
      if (bar) bar.remove();
    }
  } catch {
    pollFailCount++;
    if (pollFailCount >= 3) {
      // Show connection lost bar
      const container = document.querySelector('.chat-container');
      if (container && !container.querySelector('.chat-connection-bar')) {
        const bar = document.createElement('div');
        bar.className = 'chat-connection-bar';
        bar.textContent = 'Connection lost — retrying...';
        container.prepend(bar);
      }
    }
  }
}

async function pollTyping(serverId: string, channel: string, el: HTMLElement): Promise<void> {
  try {
    const myId = state.ownerId || state.people[0]?.id || '';
    const typers = await get<string[]>(
      `/api/messages/typing?server_id=${encodeURIComponent(serverId)}&channel=${encodeURIComponent(channel)}&person_id=${encodeURIComponent(myId)}`,
    );
    const others = typers.filter((id: string) => id !== myId);
    if (others.length === 0) {
      el.textContent = '';
      return;
    }
    const names = others.map(id => authorName(id));
    const dots = '<span class="typing-dots"><span></span><span></span><span></span></span>';
    if (names.length === 1) {
      el.innerHTML = `${dots} <strong>${esc(names[0] || '')}</strong> is typing...`;
    } else if (names.length <= 3) {
      el.innerHTML = `${dots} <strong>${names.map(esc).join(', ')}</strong> are typing...`;
    } else {
      el.innerHTML = `${dots} Several people are typing...`;
    }
  } catch { /* silent */ }
}

/** Per-message link-preview cache so re-renders don't refetch the same URLs.
 *  Keyed on the full URL; values are the resolved card payload (or null when
 *  the upstream had nothing renderable, so we don't retry on every paint). */
interface LinkPreviewCard {
  title?: string;
  description?: string;
  site_name?: string;
  image?: string;
  favicon?: string;
}
const linkPreviewCache = new Map<string, LinkPreviewCard | null>();
const SKIP_PREVIEW_SCHEMES = ['mailto:', 'javascript:', 'data:', 'file:'];

/** Lazy-load rich link preview cards under messages that contain URLs.
 *  Posts are rendered immediately; the card is appended after the meta
 *  fetch resolves so a slow upstream never blocks the message itself. */
async function loadEmbeds(container: HTMLElement): Promise<void> {
  const links = container.querySelectorAll<HTMLAnchorElement>('.msg-link');
  const seen = new Set<string>();
  for (const link of Array.from(links).slice(-10)) {
    const url = link.href;
    if (!url || seen.has(url)) continue;
    if (SKIP_PREVIEW_SCHEMES.some(s => url.toLowerCase().startsWith(s))) continue;
    if (!url.startsWith('http://') && !url.startsWith('https://')) continue;
    seen.add(url);

    const msgBody = link.closest('.msg-body');
    if (!msgBody || msgBody.querySelector('.msg-embed')) continue;

    let data: LinkPreviewCard | null | undefined = linkPreviewCache.get(url);
    if (data === undefined) {
      try {
        data = await get<LinkPreviewCard>(`/api/link-preview?url=${encodeURIComponent(url)}`);
      } catch { data = null; }
      linkPreviewCache.set(url, data ?? null);
    }
    if (!data || (!data.title && !data.description && !data.image)) continue;

    const faviconImg = data.favicon
      ? `<img class="embed-favicon" src="${esc(data.favicon)}" alt="" loading="lazy" referrerpolicy="no-referrer">`
      : '';
    const siteRow = (data.site_name || data.favicon)
      ? `<div class="embed-site">${faviconImg}${data.site_name ? esc(data.site_name) : ''}</div>`
      : '';
    const desc = data.description ? esc(data.description.substring(0, 200)) : '';

    const embed = document.createElement('div');
    embed.className = 'msg-embed';
    embed.innerHTML = `
      <div class="embed-bar"></div>
      <div class="embed-body">
        ${siteRow}
        ${data.title ? `<a class="embed-title" href="${esc(url)}" target="_blank" rel="noopener">${esc(data.title)}</a>` : ''}
        ${desc ? `<div class="embed-desc">${desc}</div>` : ''}
      </div>
      ${data.image ? `<img class="embed-thumb" src="${esc(data.image)}" alt="" loading="lazy" referrerpolicy="no-referrer">` : ''}
    `;
    msgBody.appendChild(embed);
  }
}

/** Common emoji set for quick reactions */
const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '👀', '💯', '✅', '❌', '🙏', '👏', '💪', '🤔', '😍', '🥳', '😎', '🫡', '💀'];
const EMOJI_CATEGORIES: Record<string, string[]> = {
  'Smileys': ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😌', '😍', '🥰', '😘', '😜', '🤪', '🤓', '😎', '🤩', '🥳', '😏', '😒', '😔', '😢', '😭', '😤', '🤬', '😱', '😰', '🤗', '🤔', '🫡', '💀', '👻'],
  'Gestures': ['👍', '👎', '👏', '🙌', '🤝', '🫶', '✌️', '🤞', '🫰', '👌', '🤙', '💪', '🙏', '👋', '✋', '🖐️', '🤚', '👆', '👇', '👈', '👉'],
  'Hearts': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🤎', '🖤', '🤍', '💔', '💝', '💘', '💖', '💗', '💓', '💕'],
  'Objects': ['🔥', '⭐', '🌟', '💯', '✅', '❌', '⚡', '💡', '🎉', '🎊', '🏆', '🥇', '🎯', '🚀', '💎', '🔔', '📌', '📎', '✏️', '💻'],
};

/** Compose-scoped emoji picker — inserts the chosen emoji at the textarea
 *  caret instead of firing a reaction. Shared catalog with reaction picker. */
// Exported so DMs + threads can reuse the same picker without duplicating
// 200+ lines of emoji-grid / Tenor / sticker UI. Both surfaces mount the
// shared compose component and need the same gif/sticker/emoji buttons
// wired — before 7v-pass-4 they were dead on DMs + threads.
export function showComposeEmojiPicker(anchor: HTMLElement, input: HTMLTextAreaElement): void {
  document.querySelectorAll('.emoji-picker').forEach(el => el.remove());

  const rect = anchor.getBoundingClientRect();
  const picker = document.createElement('div');
  picker.className = 'emoji-picker emoji-picker-compose';

  // Discord parity: left-rail category sidebar (with one-emoji icon per
  // category) for quick-jump scrolling. The body keeps its own scroll so
  // tapping a sidebar icon scrolls the matching category to the top.
  const CAT_ICON: Record<string, string> = {
    'Smileys': '😀', 'Gestures': '👋', 'Hearts': '❤️', 'Objects': '🔥',
  };
  const safeCatId = (cat: string): string => cat.toLowerCase().replace(/[^a-z0-9]/g, '-');

  let html = `<div class="ep-search-row">
    <input type="text" class="ep-search-input" placeholder="Search emoji" autocomplete="off">
  </div>`;
  html += '<div class="ep-shell">';
  html += '<div class="ep-cat-rail" role="tablist" aria-label="Emoji categories">';
  html += `<button type="button" class="ep-cat-rail-btn" data-cat-target="recent" title="Frequently used">⏱</button>`;
  for (const cat of Object.keys(EMOJI_CATEGORIES)) {
    html += `<button type="button" class="ep-cat-rail-btn" data-cat-target="${safeCatId(cat)}" title="${cat}">${CAT_ICON[cat] || '😀'}</button>`;
  }
  html += '</div>';
  html += '<div class="ep-body">';
  html += `<div class="ep-cat-label" data-cat-id="recent">Frequently used</div><div class="ep-grid">`;
  html += QUICK_EMOJIS.map(e => `<span class="ep-emoji" data-emoji="${e}">${e}</span>`).join('');
  html += '</div>';
  for (const [cat, emojis] of Object.entries(EMOJI_CATEGORIES)) {
    html += `<div class="ep-cat-label" data-cat-id="${safeCatId(cat)}">${cat}</div><div class="ep-grid">`;
    html += emojis.map(e => `<span class="ep-emoji" data-emoji="${e}">${e}</span>`).join('');
    html += '</div>';
  }
  html += '</div></div>';
  picker.innerHTML = html;
  document.body.appendChild(picker);

  // Position above the anchor. 340px tall × 320px wide feels Discord-ish;
  // clamp to viewport so mobile doesn't explode.
  const WIDTH = 320;
  const HEIGHT = 340;
  const top = Math.max(8, rect.top - HEIGHT - 4);
  const left = Math.min(rect.right - WIDTH, window.innerWidth - WIDTH - 8);
  picker.style.top = `${top}px`;
  picker.style.left = `${Math.max(8, left)}px`;
  picker.style.width = `${WIDTH}px`;
  picker.style.height = `${HEIGHT}px`;

  // Wire the category rail to scroll the body to the selected label.
  picker.querySelectorAll<HTMLButtonElement>('.ep-cat-rail-btn').forEach(btn => {
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      const tgt = btn.dataset['catTarget'];
      if (!tgt) return;
      const label = picker.querySelector<HTMLElement>(`.ep-cat-label[data-cat-id="${tgt}"]`);
      const body = picker.querySelector<HTMLElement>('.ep-body');
      if (label && body) {
        body.scrollTop = label.offsetTop - body.offsetTop;
      }
    });
  });

  const searchEl = picker.querySelector('.ep-search-input') as HTMLInputElement | null;
  searchEl?.focus();
  searchEl?.addEventListener('input', () => {
    const q = searchEl.value.trim().toLowerCase();
    // Simple filter: hide emoji spans whose category label doesn't match when
    // the query is non-empty. Category keywords are the cat name lowercased.
    picker.querySelectorAll<HTMLElement>('.ep-grid').forEach(g => {
      const children = Array.from(g.children) as HTMLElement[];
      let anyVisible = false;
      for (const el of children) {
        const emoji = el.dataset['emoji'] ?? '';
        const keep = !q || emoji.includes(q);
        el.style.display = keep ? '' : 'none';
        if (keep) anyVisible = true;
      }
      const prev = g.previousElementSibling as HTMLElement | null;
      if (prev?.classList.contains('ep-cat-label')) {
        prev.style.display = anyVisible ? '' : 'none';
      }
    });
  });

  picker.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const emoji = target.dataset['emoji'];
    if (!emoji) return;
    const pos = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? pos;
    input.value = input.value.substring(0, pos) + emoji + input.value.substring(end);
    const cursor = pos + emoji.length;
    input.selectionStart = input.selectionEnd = cursor;
    input.focus();
    // Trigger autoresize + mention autocomplete recompute
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });

  const close = (ev: MouseEvent): void => {
    if (!picker.contains(ev.target as Node) && ev.target !== anchor) {
      picker.remove();
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escClose);
    }
  };
  const escClose = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') {
      picker.remove();
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escClose);
    }
  };
  setTimeout(() => {
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escClose);
  }, 0);
}

/** Show emoji picker popup near a button. Discord-parity shell: title caption,
 *  search bar, category nav, keyboard arrow/enter navigation, entry animation. */
function showEmojiPicker(anchor: HTMLElement, msgId: string, _messagesEl: HTMLElement): void {
  // Remove existing picker
  document.querySelectorAll('.emoji-picker').forEach(el => el.remove());

  const picker = document.createElement('div');
  picker.className = 'emoji-picker emoji-picker-react';
  picker.setAttribute('role', 'dialog');
  picker.setAttribute('aria-label', 'Add reaction');

  // Build sections: a synthetic "Frequently used" (QUICK_EMOJIS) + categories.
  const sections: Array<{ id: string; label: string; emojis: string[] }> = [
    { id: 'freq', label: 'Frequently used', emojis: QUICK_EMOJIS },
    ...Object.entries(EMOJI_CATEGORIES).map(([label, emojis]) => ({
      id: label.toLowerCase(), label, emojis,
    })),
  ];

  const navHtml = '<div class="ep-nav" role="tablist">' + sections.map((s, i) =>
    `<button class="ep-nav-btn${i === 0 ? ' ep-nav-btn-active' : ''}" data-cat="${esc(s.id)}" type="button" aria-label="${esc(s.label)}" title="${esc(s.label)}">${esc(s.emojis[0] ?? '★')}</button>`
  ).join('') + '</div>';

  const bodyHtml = '<div class="ep-body">' + sections.map(s =>
    `<div class="ep-section" data-cat="${esc(s.id)}"><div class="ep-cat-label" id="ep-cat-${esc(s.id)}">${esc(s.label)}</div>` +
    `<div class="ep-grid" role="listbox" aria-labelledby="ep-cat-${esc(s.id)}">` +
    s.emojis.map(e => `<button class="ep-emoji" data-emoji="${esc(e)}" type="button" role="option" aria-label="${esc(e)}" tabindex="-1">${esc(e)}</button>`).join('') +
    '</div></div>'
  ).join('') + '</div>';

  const html =
    '<div class="ep-title">Emoji</div>' +
    '<div class="ep-search-row"><input type="text" class="ep-search-input" placeholder="Search emoji" autocomplete="off" aria-label="Search emoji"></div>' +
    navHtml +
    bodyHtml;

  picker.innerHTML = html;
  document.body.appendChild(picker);

  // Position after mount so we can measure the true size (title+search+nav).
  const rect = anchor.getBoundingClientRect();
  const pw = picker.offsetWidth || 320;
  const ph = picker.offsetHeight || 360;
  // Prefer above-anchor when room; fall back below. Clamp to viewport.
  let top = rect.top - ph - 6;
  if (top < 8) top = Math.min(rect.bottom + 6, window.innerHeight - ph - 8);
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - pw - 8));
  picker.style.top = `${Math.max(8, top)}px`;
  picker.style.left = `${left}px`;

  const searchEl = picker.querySelector<HTMLInputElement>('.ep-search-input');
  const bodyEl = picker.querySelector<HTMLElement>('.ep-body');
  const allEmojis = Array.from(picker.querySelectorAll<HTMLElement>('.ep-emoji'));

  // Keyboard cursor: tracks the currently "selected" emoji for Enter to fire.
  let cursorIdx = 0;
  const setCursor = (idx: number): void => {
    const visibles = allEmojis.filter(el => el.style.display !== 'none');
    if (!visibles.length) return;
    cursorIdx = Math.max(0, Math.min(idx, visibles.length - 1));
    allEmojis.forEach(el => el.classList.remove('ep-emoji-cursor'));
    const target = visibles[cursorIdx];
    if (target) {
      target.classList.add('ep-emoji-cursor');
      target.scrollIntoView({ block: 'nearest' });
    }
  };

  // Scroll-to-category on nav-tab click, and highlight the active tab as the
  // body scrolls through sections.
  const navBtns = Array.from(picker.querySelectorAll<HTMLElement>('.ep-nav-btn'));
  picker.querySelector<HTMLElement>('.ep-nav')?.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('.ep-nav-btn') as HTMLElement | null;
    if (!btn) return;
    const cat = btn.dataset['cat'];
    if (!cat) return;
    const sec = picker.querySelector<HTMLElement>(`.ep-section[data-cat="${cat}"]`);
    if (sec && bodyEl) bodyEl.scrollTo({ top: sec.offsetTop, behavior: 'smooth' });
  });
  bodyEl?.addEventListener('scroll', () => {
    if (!bodyEl) return;
    const y = bodyEl.scrollTop;
    let activeId = sections[0]?.id ?? '';
    for (const s of sections) {
      const sec = picker.querySelector<HTMLElement>(`.ep-section[data-cat="${s.id}"]`);
      if (sec && sec.offsetTop - 8 <= y) activeId = s.id;
    }
    navBtns.forEach(b => b.classList.toggle('ep-nav-btn-active', b.dataset['cat'] === activeId));
  });

  // Live filter as the user types in the search input.
  searchEl?.addEventListener('input', () => {
    const q = (searchEl.value || '').trim().toLowerCase();
    picker.querySelectorAll<HTMLElement>('.ep-section').forEach(sec => {
      const grid = sec.querySelector<HTMLElement>('.ep-grid');
      if (!grid) return;
      let anyVisible = false;
      Array.from(grid.children).forEach(child => {
        const el = child as HTMLElement;
        const emoji = el.dataset['emoji'] ?? '';
        const keep = !q || emoji.includes(q);
        el.style.display = keep ? '' : 'none';
        if (keep) anyVisible = true;
      });
      sec.style.display = anyVisible ? '' : 'none';
    });
    setCursor(0);
  });

  // Click emoji → send reaction
  const fireEmoji = (emoji: string): void => {
    const personId = state.ownerId || state.people[0]?.id || '';
    void post('/api/reactions/add', {
      message_id: msgId,
      person_id: personId,
      emoji,
    }).then(() => {
      showToast(`${emoji} added`, 'ok');
      picker.remove();
    }).catch(err => errToast(err));
  };
  picker.addEventListener('click', (e) => {
    const cell = (e.target as HTMLElement).closest('.ep-emoji') as HTMLElement | null;
    if (!cell) return;
    const emoji = cell.dataset['emoji'];
    if (emoji) fireEmoji(emoji);
  });

  // Keyboard nav: arrows move cursor, Enter fires, Escape closes.
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { picker.remove(); cleanup(); e.preventDefault(); return; }
    const visibles = allEmojis.filter(el => el.style.display !== 'none');
    if (!visibles.length) return;
    if (e.key === 'Enter') {
      const target = visibles[cursorIdx];
      const emoji = target?.dataset['emoji'];
      if (emoji) { fireEmoji(emoji); e.preventDefault(); }
      return;
    }
    // Rough column count = floor(body width / cell width)
    const cellW = visibles[0]?.offsetWidth || 36;
    const cols = Math.max(1, Math.floor((bodyEl?.clientWidth || 300) / cellW));
    if (e.key === 'ArrowRight') { setCursor(cursorIdx + 1); e.preventDefault(); }
    else if (e.key === 'ArrowLeft') { setCursor(cursorIdx - 1); e.preventDefault(); }
    else if (e.key === 'ArrowDown') { setCursor(cursorIdx + cols); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { setCursor(cursorIdx - cols); e.preventDefault(); }
  };

  // Close on outside click
  const close = (e: MouseEvent): void => {
    if (!picker.contains(e.target as Node) && e.target !== anchor) {
      picker.remove();
      cleanup();
    }
  };
  const cleanup = (): void => {
    document.removeEventListener('mousedown', close);
    document.removeEventListener('keydown', onKey);
  };
  setTimeout(() => {
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    searchEl?.focus();
    setCursor(0);
  }, 0);
}

/** Inline message editing — replace content with a textarea */
function startInlineEdit(msgId: string, container: HTMLElement): void {
  const msgEl = container.querySelector(`[data-id="${msgId}"]`);
  const contentEl = msgEl?.querySelector('.msg-content');
  if (!contentEl) return;

  const msg = messageCache.get(msgId);
  const original = msg?.content || contentEl.textContent || '';

  // Hide the hover action bar while editing so it doesn't overlay the textarea
  const actionsEl = msgEl?.querySelector<HTMLElement>('[data-msg-actions]');
  if (actionsEl) actionsEl.style.display = 'none';
  msgEl?.classList.add('msg-editing');

  /** Restore the action bar and editing state after save/cancel */
  const endEdit = (): void => {
    if (actionsEl) actionsEl.style.display = '';
    msgEl?.classList.remove('msg-editing');
  };

  contentEl.innerHTML = `
    <textarea class="inline-edit-input" id="inline-edit-${msgId}">${original.replace(/</g, '&lt;')}</textarea>
    <div class="inline-edit-hint">escape to <a class="inline-edit-cancel">cancel</a> &middot; enter to <a class="inline-edit-save">save</a></div>
  `;

  const textarea = document.getElementById(`inline-edit-${msgId}`) as HTMLTextAreaElement;
  if (textarea) {
    textarea.focus();
    textarea.selectionStart = textarea.value.length;

    // Auto-resize
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';

    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        contentEl.innerHTML = renderMarkdown(original) + (msg?.edited_at ? ` <span class="msg-edited" title="Edited ${esc(fmtFull(msg.edited_at))}">(edited)</span>` : '');
        endEdit();
      } else if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        const newContent = textarea.value.trim();
        if (newContent && newContent !== original) {
          void post('/api/messages/edit', { id: msgId, content: newContent }).then(() => {
            {
              const newTs = new Date().toISOString();
              contentEl.innerHTML = renderMarkdown(newContent) + ` <span class="msg-edited" title="Edited ${esc(fmtFull(newTs))}">(edited)</span>`;
              if (msg) { msg.content = newContent; msg.edited_at = newTs; }
            }
            endEdit();
          }).catch(err => {
            errToast(err);
            contentEl.innerHTML = renderMarkdown(original) + (msg?.edited_at ? ` <span class="msg-edited" title="Edited ${esc(fmtFull(msg.edited_at))}">(edited)</span>` : '');
            endEdit();
          });
        } else {
          contentEl.innerHTML = renderMarkdown(original) + (msg?.edited_at ? ` <span class="msg-edited" title="Edited ${esc(fmtFull(msg.edited_at))}">(edited)</span>` : '');
          endEdit();
        }
      }
    });
  }

  contentEl.querySelector('.inline-edit-cancel')?.addEventListener('click', () => {
    contentEl.innerHTML = renderMarkdown(original) + (msg?.edited_at ? ` <span class="msg-edited" title="Edited ${esc(fmtFull(msg.edited_at))}">(edited)</span>` : '');
    endEdit();
  });
  contentEl.querySelector('.inline-edit-save')?.addEventListener('click', () => {
    const ta = document.getElementById(`inline-edit-${msgId}`) as HTMLTextAreaElement;
    if (ta) {
      const ev = new KeyboardEvent('keydown', { key: 'Enter' });
      ta.dispatchEvent(ev);
    }
  });
}

/** Show image lightbox with pinch-zoom (touch) / scroll-wheel zoom (desktop) /
 *  pan when zoomed / swipe-down-to-dismiss / ESC to close. Native chat-app feel. */
function showLightbox(url: string): void {
  const overlay = document.createElement('div');
  overlay.className = 'lightbox-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Image preview');
  const openSvg = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 3h7v7"/><path d="M10 14L21 3"/><path d="M21 14v7H3V3h7"/></svg>`;
  const closeSvg = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>`;
  overlay.innerHTML = `
    <div class="lightbox-content">
      <img src="${url}" class="lightbox-img" draggable="false" alt="">
    </div>
    <div class="lightbox-chrome">
      <a href="${url}" target="_blank" rel="noopener" class="lightbox-btn lightbox-open" aria-label="Open original in a new tab" title="Open original">${openSvg}</a>
      <button type="button" class="lightbox-btn lightbox-close" aria-label="Close (Esc)">${closeSvg}</button>
    </div>
  `;
  document.body.appendChild(overlay);

  const prevActive = document.activeElement as HTMLElement | null;
  const img = overlay.querySelector('.lightbox-img') as HTMLImageElement;
  const closeBtn = overlay.querySelector('.lightbox-close') as HTMLButtonElement | null;
  closeBtn?.focus();
  let scale = 1;
  let tx = 0;
  let ty = 0;
  const MIN_SCALE = 1;
  const MAX_SCALE = 5;

  const apply = (): void => {
    img.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;
  };
  const reset = (animate: boolean): void => {
    if (animate) img.style.transition = 'transform 200ms cubic-bezier(.2,.8,.2,1)';
    scale = 1; tx = 0; ty = 0; apply();
    if (animate) setTimeout(() => { img.style.transition = ''; }, 220);
  };
  let closed = false;
  const close = (animateOut = false): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    if (animateOut) {
      overlay.classList.add('lightbox-closing');
      setTimeout(() => { overlay.remove(); prevActive?.focus?.(); }, 180);
    } else {
      overlay.remove();
      prevActive?.focus?.();
    }
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); close(true); }
    if (e.key === '0') reset(true);
    if (e.key === '+' || e.key === '=') { scale = Math.min(MAX_SCALE, scale * 1.2); apply(); }
    if (e.key === '-') { scale = Math.max(MIN_SCALE, scale / 1.2); if (scale === 1) { tx = 0; ty = 0; } apply(); }
  };
  document.addEventListener('keydown', onKey);

  overlay.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target === overlay || target.closest('.lightbox-close')) close(true);
  });

  // Desktop scroll-wheel zoom (Ctrl+wheel = zoom, plain wheel = scroll-out-to-close)
  overlay.addEventListener('wheel', (e: WheelEvent) => {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale * factor));
      if (scale === 1) { tx = 0; ty = 0; }
      apply();
    }
  }, { passive: false });

  // Double-click toggles 1x ↔ 2x
  img.addEventListener('dblclick', () => {
    if (scale > 1) reset(true);
    else { scale = 2; apply(); }
  });

  // Touch: pinch zoom + pan + swipe-down-to-dismiss
  let pinchStartDist = 0;
  let pinchStartScale = 1;
  let panStartX = 0;
  let panStartY = 0;
  let panStartTx = 0;
  let panStartTy = 0;
  let dismissStartY = 0;
  let dismissLastY = 0;
  let dismissLastT = 0;
  let dismissVy = 0;
  let mode: 'idle' | 'pinch' | 'pan' | 'dismiss' = 'idle';

  const dist = (a: Touch, b: Touch): number => {
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.hypot(dx, dy);
  };

  overlay.addEventListener('touchstart', (e: TouchEvent) => {
    if (e.touches.length === 2) {
      const t0 = e.touches[0]; const t1 = e.touches[1];
      if (!t0 || !t1) return;
      pinchStartDist = dist(t0, t1);
      pinchStartScale = scale;
      mode = 'pinch';
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      if (!t) return;
      if (scale > 1.05) {
        mode = 'pan';
        panStartX = t.clientX; panStartY = t.clientY;
        panStartTx = tx; panStartTy = ty;
      } else {
        mode = 'dismiss';
        dismissStartY = t.clientY;
        dismissLastY = t.clientY;
        dismissLastT = performance.now();
        dismissVy = 0;
      }
    }
  }, { passive: true });

  overlay.addEventListener('touchmove', (e: TouchEvent) => {
    if (mode === 'pinch' && e.touches.length === 2) {
      const t0 = e.touches[0]; const t1 = e.touches[1];
      if (!t0 || !t1) return;
      const d = dist(t0, t1);
      scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, pinchStartScale * (d / pinchStartDist)));
      apply();
    } else if (mode === 'pan' && e.touches.length === 1) {
      const t = e.touches[0];
      if (!t) return;
      tx = panStartTx + (t.clientX - panStartX);
      ty = panStartTy + (t.clientY - panStartY);
      apply();
    } else if (mode === 'dismiss' && e.touches.length === 1) {
      const t = e.touches[0];
      if (!t) return;
      const now = performance.now();
      const dt = Math.max(1, now - dismissLastT);
      dismissVy = (t.clientY - dismissLastY) / dt;
      dismissLastY = t.clientY;
      dismissLastT = now;
      const dy = t.clientY - dismissStartY;
      if (dy > 8) {
        const fade = Math.max(0, 1 - dy / 400);
        overlay.style.background = `rgba(0,0,0,${0.92 * fade})`;
        img.style.transform = `translate3d(0, ${dy}px, 0) scale(${1 - dy * 0.0008})`;
      } else if (dy < 0) {
        overlay.style.background = '';
        img.style.transform = '';
      }
    }
  }, { passive: true });

  overlay.addEventListener('touchend', (e: TouchEvent) => {
    if (mode === 'dismiss') {
      const last = (e.changedTouches[0]?.clientY ?? 0) - dismissStartY;
      // Distance OR velocity triggers dismiss; matches Discord/iOS sheet feel.
      if (last > 100 || (last > 40 && dismissVy > 0.6)) { close(true); return; }
      // Snap back
      overlay.style.background = '';
      reset(true);
    } else if (mode === 'pinch' && scale < 1.05) {
      reset(true);
    }
    if (e.touches.length === 0) mode = 'idle';
  }, { passive: true });
}

/** GIF picker (Discord parity, scaffold). Backed by Tenor v2 once a server
 *  owner sets a Tenor API key; falls back to a setup-prompt panel until then.
 *  We default to Tenor because Google issues free keys (sign-up required) and
 *  v2 is the actively-maintained API. Per Mitchell: scaffold the UI now,
 *  per-server-owner key configuration later. Sticker picker reuses the
 *  same shape once the file format question is settled. */
export async function openGifPicker(anchor: HTMLElement, inputEl: HTMLTextAreaElement): Promise<void> {
  document.querySelectorAll('.gif-popup').forEach(el => el.remove());

  // Server-scoped Tenor key, persisted client-side until a backend setting
  // is wired. Server owners can paste their own Google Cloud Tenor v2 key
  // here \u2014 it's stored locally so it never leaves their browser without
  // their say-so, and only fires Tenor API requests when set.
  const sid = state.currentServer || 'default';
  const tenorKey = localStorage.getItem(`hub-people-tenor-key-${sid}`) || '';

  const popup = document.createElement('div');
  popup.className = 'gif-popup';
  popup.setAttribute('role', 'dialog');
  popup.setAttribute('aria-label', 'GIF picker');
  popup.innerHTML = `
    <div class="gif-popup-header">
      <input class="gif-popup-search" type="text" placeholder="Search Tenor for a GIF\u2026" id="gif-search" autocomplete="off">
      <button class="gif-popup-close" id="gif-close" aria-label="Close">&times;</button>
    </div>
    <div class="gif-popup-body" id="gif-body">
      ${tenorKey ? '<div class="gif-empty">Type a search term above.</div>' : `
        <div class="gif-setup">
          <div class="gif-setup-title">GIF picker not configured</div>
          <div class="gif-setup-hint">Server owners can add a free <a href="https://developers.google.com/tenor/guides/quickstart" target="_blank" rel="noopener">Tenor API key</a> from Google to enable GIF search. Until then, paste a GIF URL directly into the message box.</div>
          <input type="text" class="gif-setup-input" id="gif-key-input" placeholder="Paste Tenor API key (server-scoped)" autocomplete="off">
          <button class="gif-setup-save" id="gif-key-save" type="button">Save key for this server</button>
          <div class="gif-setup-note">Stored in this browser only. We never send the key anywhere unless you search.</div>
        </div>
      `}
    </div>
  `;
  document.body.appendChild(popup);

  // Position above the GIF button (compose actions row)
  const r = anchor.getBoundingClientRect();
  popup.style.position = 'fixed';
  popup.style.right = `${Math.max(8, window.innerWidth - r.right)}px`;
  popup.style.bottom = `${Math.max(8, window.innerHeight - r.top + 8)}px`;

  const close = (): void => { popup.remove(); document.removeEventListener('keydown', onKey); };
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', onKey);
  popup.querySelector('#gif-close')?.addEventListener('click', close);

  // Save key flow (when no key is set)
  popup.querySelector<HTMLButtonElement>('#gif-key-save')?.addEventListener('click', () => {
    const v = (popup.querySelector('#gif-key-input') as HTMLInputElement | null)?.value.trim() || '';
    if (!v) { showToast('Paste a Tenor API key first', 'warn'); return; }
    localStorage.setItem(`hub-people-tenor-key-${sid}`, v);
    showToast('Tenor key saved for this server', 'ok');
    close();
  });

  if (!tenorKey) return;

  // Active picker flow: type search query, fetch from Tenor v2, render grid,
  // click \u2192 insert URL into compose. 8-result cap to keep things light.
  const searchEl = popup.querySelector<HTMLInputElement>('#gif-search');
  const bodyEl = popup.querySelector('#gif-body');
  if (!searchEl || !bodyEl) return;
  searchEl.focus();
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  searchEl.addEventListener('input', () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const q = searchEl.value.trim();
    if (!q) { bodyEl.innerHTML = '<div class="gif-empty">Type a search term above.</div>'; return; }
    debounceTimer = setTimeout(() => { void runSearch(q); }, 300);
  });

  async function runSearch(q: string): Promise<void> {
    if (!bodyEl) return;
    bodyEl.innerHTML = '<div class="gif-empty">Searching\u2026</div>';
    try {
      const url = `https://tenor.googleapis.com/v2/search?q=${encodeURIComponent(q)}&key=${encodeURIComponent(tenorKey)}&limit=8&media_filter=tinygif`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`tenor ${r.status}`);
      type TenorResult = { results: { id: string; media_formats: Record<string, { url: string }> }[] };
      const data = await r.json() as TenorResult;
      if (!data.results?.length) { bodyEl.innerHTML = '<div class="gif-empty">No GIFs found.</div>'; return; }
      bodyEl.innerHTML = `<div class="gif-grid">${data.results.map(g => {
        const tinyUrl = g.media_formats?.['tinygif']?.url ?? '';
        return tinyUrl ? `<button type="button" class="gif-cell" data-gif-url="${esc(tinyUrl)}"><img src="${esc(tinyUrl)}" alt="" loading="lazy"></button>` : '';
      }).join('')}</div>`;
      bodyEl.querySelectorAll<HTMLButtonElement>('.gif-cell').forEach(btn => {
        btn.addEventListener('click', () => {
          const gifUrl = btn.dataset['gifUrl'] ?? '';
          if (!gifUrl) return;
          // Append the GIF URL on its own line so the unfurler / image preview
          // picks it up. Pure text \u2014 no special protocol.
          inputEl.value = (inputEl.value.trim() + '\n' + gifUrl).trim();
          inputEl.dispatchEvent(new Event('input', { bubbles: true }));
          close();
          inputEl.focus();
        });
      });
    } catch {
      bodyEl.innerHTML = '<div class="gif-empty">Tenor search failed. Check the key in Server Settings.</div>';
    }
  }
}

/** Sticker picker (Discord parity, no Nitro gate). Free tier: a built-in
 *  set of 24 large emoji acting as stickers (rendered at 64px so the
 *  feel matches Discord's sticker grid). Server-owner Tenor sticker key
 *  support layered on top later via the same per-server-key pattern as
 *  GIF picker. */
const FREE_STICKERS = [
  '\u{1F389}', '\u{1F38A}', '\u{1F386}', '\u{1F387}', '\u{2728}', '\u{1F31F}',
  '\u{1F525}', '\u{1F4AF}', '\u{1F44D}', '\u{1F44F}', '\u{1F64C}', '\u{1F91D}',
  '\u{2764}\u{FE0F}', '\u{1F49B}', '\u{1F49A}', '\u{1F499}', '\u{1F49C}', '\u{1F90D}',
  '\u{1F602}', '\u{1F605}', '\u{1F923}', '\u{1F60E}', '\u{1F914}', '\u{1F9D0}',
];

export async function openStickerPicker(anchor: HTMLElement, inputEl: HTMLTextAreaElement): Promise<void> {
  document.querySelectorAll('.stk-popup').forEach(el => el.remove());

  const popup = document.createElement('div');
  popup.className = 'stk-popup';
  popup.setAttribute('role', 'dialog');
  popup.setAttribute('aria-label', 'Sticker picker');
  popup.innerHTML = `
    <div class="stk-popup-header">
      <span class="stk-popup-title">Stickers</span>
      <button class="stk-popup-close" id="stk-close" aria-label="Close">&times;</button>
    </div>
    <div class="stk-popup-body">
      <div class="stk-section-title">Free pack</div>
      <div class="stk-grid">
        ${FREE_STICKERS.map(s => `<button type="button" class="stk-cell" data-stk="${esc(s)}">${esc(s)}</button>`).join('')}
      </div>
      <div class="stk-section-title">Custom packs</div>
      <div class="stk-empty">Custom sticker packs require a server-owner Giphy or Tenor sticker key. Coming with the same per-server-key flow as GIFs.</div>
    </div>
  `;
  document.body.appendChild(popup);

  const r = anchor.getBoundingClientRect();
  popup.style.position = 'fixed';
  popup.style.right = `${Math.max(8, window.innerWidth - r.right)}px`;
  popup.style.bottom = `${Math.max(8, window.innerHeight - r.top + 8)}px`;

  const close = (): void => { popup.remove(); document.removeEventListener('keydown', onKey); };
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', onKey);
  popup.querySelector('#stk-close')?.addEventListener('click', close);

  popup.querySelectorAll<HTMLButtonElement>('.stk-cell').forEach(btn => {
    btn.addEventListener('click', () => {
      const s = btn.dataset['stk'] ?? '';
      if (!s) return;
      // Insert sticker as a single line wrapped in :sticker: marker so we can
      // optionally render bigger in the message renderer later. For now it
      // appears inline as a bigger-feeling emoji glyph.
      inputEl.value = (inputEl.value.trim() + '\n' + s).trim();
      inputEl.dispatchEvent(new Event('input', { bubbles: true }));
      close();
      inputEl.focus();
    });
  });
}

/** Clean up polling + realtime subscription when leaving channel */
export function cleanupChat(): void {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  teardownVirtualization();
  for (const off of realtimeUnsubs) off();
  realtimeUnsubs = [];
  if (activeChannelTopic) {
    realtime.unsubscribeChannel(activeChannelTopic);
    activeChannelTopic = '';
  }
  // Save scroll position of the channel we're leaving. (lastRead advancement
  // happens at the start of renderChat, gated on a real channel switch — see
  // M8-R4: writing here fired on every re-render and wiped the seed.)
  if (currentChannelKey) {
    const msgsEl = document.getElementById('chat-messages');
    if (msgsEl) channelScrollPositions.set(currentChannelKey, msgsEl.scrollTop);
  }
  if (replyKeyHandler) { document.removeEventListener('keydown', replyKeyHandler); replyKeyHandler = null; }
  composeHandle?.destroy();
  composeHandle = null;
  closeThreadPanel();
  const cleanup = (window as unknown as Record<string, unknown>)['__hpCleanup'] as (() => void) | undefined;
  cleanup?.();
}
